import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  User,
  Send,
  IndianRupee,
  Calendar,
  CalendarPlus,
  Mail,
  FileText,
  Loader2,
  ShieldCheck,
  Eye,
  X,
  CheckCircle,
  XCircle,
  HelpCircle,
  Edit2,
  Video,
  Plus,
  Reply,
  Clock,
  ExternalLink,
  MessageSquare,
  MailOpen,
  Inbox,
  Zap,
  AlertCircle,
  Sparkles,
  Pencil,
  Save,
  RotateCcw,
} from 'lucide-react';
import { isApplicationStatusConflictError } from '@core/supabase/applicationStatusConflict';
import { applicationService } from '@core/supabase/services/applicationService';
import { activityService } from '@core/supabase/services/activityService';
import { orgService } from '@core/supabase/services/orgService';
import { optimizationService, B2BOptimization } from '@core/supabase/services/optimizationService';
import { candidateService } from '@core/supabase/services/candidateService';
import { emailService } from '@core/supabase/services/emailService';
import { interviewEventService, InterviewEvent } from '@core/supabase/services/interviewEventService';
import { downloadICS } from '@core/utils/icsGenerator';
import { InterviewMonthCalendar } from '@modules/interviews/InterviewMonthCalendar';
import {
  buildApplicationHeartbeatTimeline,
  ApplicationHeartbeatTimeline,
} from '@modules/applications/applicationHeartbeatTimeline';
import {
  scorecardService,
  InterviewScorecard,
  ScorecardRecommendation,
  ScorecardCreate,
  DEFAULT_RUBRIC,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_COLORS,
} from '@core/supabase/services/scorecardService';
import {
  Application, ApplicationStatus, ApplicationStatusHistory,
  OrgUser, EmailThread, CTAAction, MaskingTemplate,
  REJECTION_REASON_CODES, REJECTION_REASON_LABELS, RejectionReasonCode,
} from '@core/types';
import { maskingService } from '@core/supabase/services/maskingService';
import { maskResumeBodyText } from '@core/masking/maskResumeBodyText';
import {
  OptimizedResumePreviewModal,
  type OptimizedResumePreviewModalPayload,
} from '@modules/role-fit/OptimizedResumePreviewModal';
import { loadResumePreviewPayloadFromStoragePath } from '@modules/role-fit/fetchResumePreviewPayload';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordDetailLayout } from '@shared/components/ui/RecordDetailLayout';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { RightRailWithAssistant } from '@shared/components/ui/RightRailWithAssistant';
import { useTrackRecentlyViewed } from '@shared/hooks/useRecentlyViewed';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { detailTbBtn, detailTbNeutral, detailTbDanger, detailTbEdit } from '@shared/lib/detailToolbarStyles';
import { ApplicationStatusChip } from '@shared/components/ui/StatusChip';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { useAuth } from '@core/auth/useAuth';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { formatDateTimeGmt, formatDateOrDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

// Allowed forward transitions per status (state machine)
const VALID_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  submitted:              ['viewed_by_client', 'shortlisted', 'rejected', 'on_hold'],
  viewed_by_client:       ['shortlisted', 'rejected', 'on_hold'],
  shortlisted:            ['interview_1_scheduled', 'rejected', 'on_hold'],
  interview_1_scheduled:  ['interview_1_conducted', 'no_show', 'rejected', 'on_hold'],
  interview_1_conducted:  ['interview_2_scheduled', 'offer_extended', 'rejected', 'on_hold'],
  interview_2_scheduled:  ['interview_2_conducted', 'no_show', 'rejected', 'on_hold'],
  interview_2_conducted:  ['offer_extended', 'rejected', 'on_hold'],
  offer_extended:         ['offer_accepted', 'offer_rejected', 'on_hold'],
  offer_accepted:         ['placed'],
  offer_rejected:         ['closed'],
  placed:                 ['closed'],
  rejected:               ['closed'],
  no_show:                ['interview_1_scheduled', 'interview_2_scheduled', 'rejected', 'closed'],
  on_hold:                ['submitted', 'shortlisted', 'interview_1_scheduled', 'closed'],
  closed:                 [],
};

function recordInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Submitted', viewed_by_client: 'Viewed by Client', shortlisted: 'Shortlisted',
  interview_1_scheduled: 'Interview 1 Scheduled', interview_1_conducted: 'Interview 1 Conducted',
  interview_2_scheduled: 'Interview 2 Scheduled', interview_2_conducted: 'Interview 2 Conducted',
  offer_extended: 'Offer Extended', offer_accepted: 'Offer Accepted', offer_rejected: 'Offer Rejected',
  placed: 'Placed ✓', rejected: 'Rejected', no_show: 'No Show', on_hold: 'On Hold', closed: 'Closed',
};

const CTA_ICON: Record<CTAAction, React.ReactNode> = {
  accept: <CheckCircle className="w-3.5 h-3.5 text-green-400" />,
  reject: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  query:  <HelpCircle className="w-3.5 h-3.5 text-yellow-400" />,
};
const CTA_LABEL: Record<CTAAction, string> = { accept: 'Shortlisted', reject: 'Passed', query: 'Query' };

function formatTimelineDate(date: string) {
  return formatDateTimeGmt(date);
}

/** Sort key: scheduled time when set, else created (chronological). */
function interviewSortTime(ev: InterviewEvent): number {
  if (ev.scheduled_at) return new Date(ev.scheduled_at).getTime();
  return new Date(ev.created_at).getTime();
}

function formatInterviewRecordDate(ev: InterviewEvent): string {
  if (ev.scheduled_at) {
    return formatDateTimeGmt(ev.scheduled_at);
  }
  return `TBD · added ${formatDateTimeGmt(ev.created_at)}`;
}

function dayToDatetimeLocalSeed(day: Date): string {
  const y = day.getFullYear();
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T09:00`;
}

function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const VALID_TABS = ['overview', 'emails', 'fitment', 'interviews', 'parameters'] as const;
type TabId = (typeof VALID_TABS)[number];

/** PRD §11.1 — offer draft AI from post–round-1 through placement */
const OFFER_STAGE_STATUSES: ApplicationStatus[] = [
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
  'placed',
];

function applicationResumeStoragePath(app: Application, optimization: B2BOptimization | null): string {
  return (
    optimization?.optimized_resume_url?.trim() ||
    app.candidate?.base_resume_url?.trim() ||
    ''
  );
}

/** Module-level: stable component identity so inputs in overview edit mode don't remount each keystroke. */
function ApplicationDetailAttrRow({
  label,
  children,
  ddClassName,
}: {
  label: string;
  children: React.ReactNode;
  ddClassName?: string;
}) {
  return (
    <>
      <dt className="vscode-text-secondary pr-2 min-w-0">{label}</dt>
      <dd className={`vscode-text-primary min-w-0 break-words ${ddClassName ?? ''}`}>{children}</dd>
    </>
  );
}

function ApplicationDetailAttrGroup({ title, children, icon }: { title: ReactNode; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="rounded-xl border vscode-border vscode-bg-secondary overflow-hidden">
      <div className="px-4 py-2.5 border-b vscode-border flex items-center gap-2">
        {icon && <span className="text-emerald-400">{icon}</span>}
        <span className="text-xs font-semibold uppercase tracking-wider vscode-text-primary">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [app, setApp] = useState<Application | null>(null);

  useTrackRecentlyViewed(
    useMemo(
      () =>
        app
          ? {
              kind: 'application' as const,
              id: app.id,
              label: app.candidate?.full_name || app.candidate?.email || 'Application',
              sublabel: [app.job?.title, app.status?.replace(/_/g, ' ')].filter(Boolean).join(' · ') || undefined,
              to: `/applications/${app.id}`,
            }
          : null,
      [app?.id, app?.candidate?.full_name, app?.candidate?.email, app?.job?.title, app?.status],
    ),
  );
  const [history, setHistory] = useState<ApplicationStatusHistory[]>([]);
  const [teamMembers, setTeamMembers] = useState<OrgUser[]>([]);
  const [optimization, setOptimization] = useState<B2BOptimization | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusNote, setStatusNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusConcurrencyMessage, setStatusConcurrencyMessage] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [pendingRejectStatus, setPendingRejectStatus] = useState<ApplicationStatus | null>(null);
  const [rejectCode, setRejectCode] = useState<RejectionReasonCode>('not_qualified');
  const [rejectText, setRejectText] = useState('');
  const [placementFee, setPlacementFee] = useState('');
  const [savingFee, setSavingFee] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [loadingMaskedPreview, setLoadingMaskedPreview] = useState(false);
  const [loadingFullPreview, setLoadingFullPreview] = useState(false);
  type MaskedResumePreviewState = {
    payload: OptimizedResumePreviewModalPayload;
    headline: string;
    eyebrow?: string;
  };
  const [maskedResumePreview, setMaskedResumePreview] = useState<MaskedResumePreviewState | null>(null);
  const maskedPdfBlobUrlRef = useRef<string | null>(null);
  const [emailThreads, setEmailThreads] = useState<EmailThread[]>([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [offerNotes, setOfferNotes] = useState('');
  const [savingOfferNotes, setSavingOfferNotes] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
  const [emailPage, setEmailPage] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(5);

  // Interview events state (EML-060)
  const [interviewEvents, setInterviewEvents] = useState<InterviewEvent[]>([]);
  const [interviewCalMonth, setInterviewCalMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [interviewCalSelectedEventId, setInterviewCalSelectedEventId] = useState<string | null>(null);
  const [intFormOpen, setIntFormOpen] = useState(false);
  const [intForm, setIntForm] = useState({ round: 1, scheduled_at: '', location: '', interviewer_name: '', interviewer_email: '', notes: '' });
  const [savingInt, setSavingInt] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [resendInviteOnEdit, setResendInviteOnEdit] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [deleteInterviewRoundOpen, setDeleteInterviewRoundOpen] = useState(false);

  // Scorecard state (ACT-010)
  const [scorecards, setScorecards] = useState<InterviewScorecard[]>([]);
  const [scorecardFormOpen, setScorecardFormOpen] = useState(false);
  const [scorecardRound, setScorecardRound] = useState('Round 1');
  const [scorecardScore, setScorecardScore] = useState<number | ''>('');
  const [scorecardRec, setScorecardRec] = useState<ScorecardRecommendation | ''>('');
  const [scorecardNotes, setScorecardNotes] = useState('');
  const [scorecardRubric, setScorecardRubric] = useState(DEFAULT_RUBRIC.map((r) => ({ ...r })));
  const [aiAssistField, setAiAssistField] = useState<null | 'internal' | 'interview' | 'scorecard' | 'offer' | 'screening'>(
    null
  );

  // Email thread detail modal
  const [emailDetailThread, setEmailDetailThread] = useState<EmailThread | null>(null);
  const [emailAccordionOpen, setEmailAccordionOpen] = useState<Record<string, boolean>>({ body: true, cta: false, track: false, replies: false });
  const [savingScorecard, setSavingScorecard] = useState(false);

  /** §10.2: short-lived pipeline edit lease while this page is open */
  const [pipelineLockHeld, setPipelineLockHeld] = useState(false);
  const [pipelineLockedByPeer, setPipelineLockedByPeer] = useState<string | null>(null);
  const [pipelineLockBootstrapError, setPipelineLockBootstrapError] = useState<string | null>(null);

  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    VALID_TABS.includes(tabFromUrl ?? ('' as TabId)) ? tabFromUrl! : 'overview'
  );
  useEffect(() => {
    const tab = searchParams.get('tab') as TabId | null;
    if (tab && VALID_TABS.includes(tab)) setActiveTab(tab);
    else if (!tab) setActiveTab('overview');
  }, [searchParams]);
  const setTab = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
        next.delete('edit');
      }
      return next;
    }, { replace: true });
  };

  const editQuery = searchParams.get('edit');
  useEffect(() => {
    if (activeTab !== 'overview' && editQuery === '1') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
  }, [activeTab, editQuery, setSearchParams]);

  useEffect(() => {
    if (!id || !user?.orgId) {
      setPipelineLockHeld(false);
      setPipelineLockedByPeer(null);
      setPipelineLockBootstrapError(null);
      return;
    }

    let cancelled = false;
    const lockHeldRef = { current: false };

    (async () => {
      if (!user.orgUserId) {
        setPipelineLockBootstrapError(
          'Your profile is not linked to this organisation; pipeline co-editing protection is limited.',
        );
        setPipelineLockedByPeer(null);
        setPipelineLockHeld(false);
        return;
      }
      setPipelineLockBootstrapError(null);
      const r = await applicationService.acquirePipelineLock(id, user.orgUserId);
      if (cancelled) {
        if (r.ok) {
          await applicationService.releasePipelineLock(id, user.orgUserId).catch(() => {});
        }
        return;
      }
      if (r.ok) {
        lockHeldRef.current = true;
        setPipelineLockHeld(true);
        setPipelineLockedByPeer(null);
      } else if (r.reason === 'locked') {
        setPipelineLockHeld(false);
        setPipelineLockedByPeer(r.lockedByDisplayName ?? 'Another recruiter');
      } else {
        setPipelineLockHeld(false);
        setPipelineLockedByPeer(null);
      }
    })();

    return () => {
      cancelled = true;
      if (user.orgUserId && lockHeldRef.current) {
        lockHeldRef.current = false;
        void applicationService.releasePipelineLock(id, user.orgUserId).catch(() => {});
      }
      setPipelineLockHeld(false);
    };
  }, [id, user?.orgId, user?.orgUserId]);

  useEffect(() => {
    if (!pipelineLockHeld || !id || !user?.orgUserId) return;
    const iv = window.setInterval(() => {
      void applicationService.acquirePipelineLock(id, user.orgUserId!, 120).catch(() => {});
    }, 45_000);
    return () => window.clearInterval(iv);
  }, [pipelineLockHeld, id, user?.orgUserId]);

  useEffect(() => {
    if (!id || !user?.orgId) return;
    const storageKey = 'ggj.b2b.applicationDetail.id';
    const lastId = sessionStorage.getItem(storageKey);
    if (lastId !== id) {
      sessionStorage.setItem(storageKey, id);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
    Promise.all([
      applicationService.getById(id),
      applicationService.getStatusHistory(id),
      orgService.getTeamMembers(user.orgId),
      optimizationService.getByApplication(id),
      emailService.listByApplication(id),
      scorecardService.listByApplication(id),
      interviewEventService.listByApplication(id),
    ]).then(([a, h, tm, opt, threads, cards, events]) => {
      setApp(a);
      setHistory(h);
      setTeamMembers(tm);
      setOptimization(opt);
      setEmailThreads(threads ?? []);
      setScorecards(cards ?? []);
      setInterviewEvents(events ?? []);
      if (a?.placement_fee) setPlacementFee(String(a.placement_fee));
      if (a?.internal_notes !== undefined) setInternalNotes(a.internal_notes ?? '');
      if (a?.offer_notes !== undefined) setOfferNotes(a.offer_notes ?? '');
    }).finally(() => setLoading(false));
  }, [id, user?.orgId, setSearchParams]);

  const REJECT_STATUSES: ApplicationStatus[] = ['rejected', 'no_show'];

  const handleStatusChange = async (newStatus: ApplicationStatus) => {
    if (!id || !app || !user?.orgId) return;
    if (REJECT_STATUSES.includes(newStatus)) {
      setPendingRejectStatus(newStatus);
      setRejectCode('not_qualified');
      setRejectText('');
      setRejectModalOpen(true);
      return;
    }
    await doStatusUpdate(newStatus);
  };

  const doStatusUpdate = async (
    newStatus: ApplicationStatus,
    reasonCode?: RejectionReasonCode,
    reasonText?: string
  ) => {
    if (!id || !app || !user?.orgId) return;
    if (pipelineLockedByPeer) {
      setStatusConcurrencyMessage(
        `${pipelineLockedByPeer} is editing this application. Pipeline status is locked until they finish or the lease expires (~2 minutes).`,
      );
      return;
    }
    setUpdatingStatus(true);
    setStatusConcurrencyMessage(null);
    try {
      const { updated_at } = await applicationService.updateStatus(
        id,
        newStatus,
        statusNote || undefined,
        reasonCode,
        reasonText,
        { expectedUpdatedAt: app.updated_at },
      );
      activityService.log(user.orgId, 'application', 'status_changed', {
        entityId: id,
        actorId: user.orgUserId ?? undefined,
        actorKeycloak: user.id,
        payload: { from_status: app.status, to_status: newStatus, application_id: id },
      });
      setApp((a) => a ? {
        ...a,
        status: newStatus,
        updated_at,
        ...(reasonCode ? { rejection_reason_code: reasonCode } : {}),
        ...(reasonText  ? { rejection_reason_text:  reasonText  } : {}),
      } : a);
      setHistory((h) => [...h, {
        id: crypto.randomUUID(),
        application_id: id,
        from_status: app.status,
        to_status: newStatus,
        changed_by: user?.id ?? null,
        changed_at: new Date().toISOString(),
        note: statusNote || null,
      }]);
      setStatusNote('');
    } catch (e) {
      if (isApplicationStatusConflictError(e)) {
        setStatusConcurrencyMessage(e.message);
        const fresh = await applicationService.getById(id);
        setApp(fresh);
        try {
          setHistory(await applicationService.getStatusHistory(id));
        } catch {
          /* ignore */
        }
        return;
      }
      console.error(e);
      setStatusConcurrencyMessage(e instanceof Error ? e.message : 'Could not update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingRejectStatus) return;
    setRejectModalOpen(false);
    await doStatusUpdate(pendingRejectStatus, rejectCode, rejectText || undefined);
    setPendingRejectStatus(null);
  };

  const handleAssign = async (orgUserId: string) => {
    if (!id || !user?.orgId) return;
    await applicationService.assign(id, orgUserId || null);
    activityService.log(user.orgId, 'application', 'assigned', {
      entityId: id,
      actorId: user.orgUserId ?? undefined,
      payload: { application_id: id, assigned_to: orgUserId || null },
    });
    const member = teamMembers.find((m) => m.id === orgUserId);
    setApp((a) => a ? { ...a, assigned_to: orgUserId || null, assigned_user: member } : a);
  };

  const handleGeneratePdf = async () => {
    if (!id) return;
    setGeneratingPdf(true);
    try {
      const result = await optimizationService.generateMaskedPdf(id);
      setOptimization((prev) => prev
        ? { ...prev, masked_resume_url: result.masked_resume_url, alias_used: result.alias, watermark_ref: result.watermark_ref }
        : { id: '', org_id: '', application_id: id, candidate_id: '', job_id: '',
            b2c_optimization_id: null, optimized_resume_url: null, ats_score_before: null, ats_score_after: null, masking_template_id: null, created_at: '',
            masked_resume_url: result.masked_resume_url, alias_used: result.alias, watermark_ref: result.watermark_ref });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const closeMaskedResumePreview = () => {
    if (maskedPdfBlobUrlRef.current) {
      URL.revokeObjectURL(maskedPdfBlobUrlRef.current);
      maskedPdfBlobUrlRef.current = null;
    }
    setMaskedResumePreview(null);
  };

  const resolveMaskingTemplate = async (): Promise<MaskingTemplate | null> => {
    if (!optimization || !user?.orgId || !app) return null;
    let template: MaskingTemplate | null = null;
    if (optimization.masking_template_id) {
      template = await maskingService.getTemplate(optimization.masking_template_id);
    }
    if (!template && app?.client?.default_masking_template_id) {
      template = await maskingService.getTemplate(app.client.default_masking_template_id);
    }
    if (!template) {
      template = await maskingService.getDefaultTemplate(user.orgId);
    }
    return template;
  };

  const handlePreviewMaskedResume = async () => {
    if (!optimization?.masked_resume_url || !app || !user?.orgId) return;
    const c = app.candidate;
    if (!c) return;

    setLoadingMaskedPreview(true);
    if (maskedPdfBlobUrlRef.current) {
      URL.revokeObjectURL(maskedPdfBlobUrlRef.current);
      maskedPdfBlobUrlRef.current = null;
    }

    try {
      const template = await resolveMaskingTemplate();
      const resumePath = applicationResumeStoragePath(app, optimization);

      const tryTextPreview = resumePath.length > 0 && !/\.pdf$/i.test(resumePath);

      if (tryTextPreview) {
        try {
          const result = await loadResumePreviewPayloadFromStoragePath(resumePath);
          if (result.mode === 'modal' && result.payload.kind === 'text') {
            const masked = maskResumeBodyText(result.payload.body, {
              candidate: c,
              template,
            });
            const headline =
              [app.job?.title, app.client?.name].filter(Boolean).join(' — ') ||
              c.full_name ||
              'Resume';
            setMaskedResumePreview({
              payload: {
                kind: 'text',
                body: masked,
                templateId: result.payload.templateId,
                design: result.payload.design,
              },
              headline,
              eyebrow: 'Client-safe preview (contact details partially masked)',
            });
            return;
          }
        } catch {
          /* fall through to PDF blob */
        }
      }

      const signed = await optimizationService.getMaskedResumeUrl(optimization.masked_resume_url);
      const res = await fetch(signed);
      if (!res.ok) throw new Error(`Could not load masked PDF (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      maskedPdfBlobUrlRef.current = blobUrl;
      const headline =
        [app.job?.title, app.client?.name].filter(Boolean).join(' — ') || 'Masked resume';
      setMaskedResumePreview({
        payload: { kind: 'pdf', url: blobUrl },
        headline,
        eyebrow: 'Client-safe PDF',
      });
    } catch (err) {
      console.error('Failed to load masked preview:', err);
    } finally {
      setLoadingMaskedPreview(false);
    }
  };

  const handlePreviewFullResume = async () => {
    if (!app?.candidate) return;
    const path = applicationResumeStoragePath(app, optimization);
    if (!path) return;

    setLoadingFullPreview(true);
    if (maskedPdfBlobUrlRef.current) {
      URL.revokeObjectURL(maskedPdfBlobUrlRef.current);
      maskedPdfBlobUrlRef.current = null;
    }

    try {
      const c = app.candidate;
      const headline =
        [app.job?.title, app.client?.name].filter(Boolean).join(' — ') || c.full_name || 'Resume';

      if (/\.pdf$/i.test(path)) {
        const href = await candidateService.getResumeSignedUrl(path);
        const res = await fetch(href);
        if (!res.ok) throw new Error(`Could not load resume (${res.status})`);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        maskedPdfBlobUrlRef.current = blobUrl;
        setMaskedResumePreview({
          payload: { kind: 'pdf', url: blobUrl },
          headline,
          eyebrow: 'Original resume (internal — full detail)',
        });
        return;
      }

      const result = await loadResumePreviewPayloadFromStoragePath(path);
      if (result.mode === 'external') {
        window.open(result.href, '_blank', 'noopener,noreferrer');
        return;
      }
      if (result.mode === 'modal' && result.payload.kind === 'text') {
        setMaskedResumePreview({
          payload: {
            kind: 'text',
            body: result.payload.body,
            templateId: result.payload.templateId,
            design: result.payload.design,
          },
          headline,
          eyebrow: 'Original resume (internal — full detail)',
        });
        return;
      }
      if (result.mode === 'modal' && result.payload.kind === 'pdf') {
        const res = await fetch(result.payload.url);
        if (!res.ok) throw new Error(`Could not load resume (${res.status})`);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        maskedPdfBlobUrlRef.current = blobUrl;
        setMaskedResumePreview({
          payload: { kind: 'pdf', url: blobUrl },
          headline,
          eyebrow: 'Original resume (internal — full detail)',
        });
      }
    } catch (err) {
      console.error('Failed to load full resume preview:', err);
    } finally {
      setLoadingFullPreview(false);
    }
  };

  const openEditEvent = (ev: InterviewEvent) => {
    setEditingEventId(ev.id);
    setIntForm({
      round: ev.round,
      scheduled_at: ev.scheduled_at ? isoToDatetimeLocal(ev.scheduled_at) : '',
      location: ev.location ?? '',
      interviewer_name: ev.interviewer_name ?? '',
      interviewer_email: ev.interviewer_email ?? '',
      notes: ev.notes ?? '',
      ...(ev.duration_minutes ? { duration: ev.duration_minutes } : { duration: 60 }),
    } as typeof intForm & { duration: number });
    setResendInviteOnEdit(false);
    setIntFormOpen(true);
  };

  const closeIntForm = () => {
    setIntFormOpen(false);
    setEditingEventId(null);
    setResendInviteOnEdit(false);
  };

  const handleSaveFee = async () => {
    if (!id || !placementFee) return;
    setSavingFee(true);
    await applicationService.updatePlacement(
      id,
      parseFloat(placementFee),
      app?.placement_date ?? new Date().toISOString().split('T')[0]
    );
    setSavingFee(false);
  };

  const { timeline, nextActionIndex } = useMemo(
    () => buildApplicationHeartbeatTimeline(history, emailThreads),
    [history, emailThreads]
  );

  const paginatedEmailThreads = useMemo(() => {
    const start = (emailPage - 1) * emailPageSize;
    return emailThreads.slice(start, start + emailPageSize);
  }, [emailThreads, emailPage, emailPageSize]);

  const sortedInterviewEvents = useMemo(
    () => [...interviewEvents].sort((a, b) => interviewSortTime(a) - interviewSortTime(b)),
    [interviewEvents]
  );

  const interviewMonthCalendarEvents = useMemo(() => {
    const start = new Date(interviewCalMonth.getFullYear(), interviewCalMonth.getMonth(), 1);
    const end = new Date(interviewCalMonth.getFullYear(), interviewCalMonth.getMonth() + 1, 0, 23, 59, 59, 999);
    return interviewEvents
      .filter((r) => r.scheduled_at)
      .filter((r) => {
        const t = new Date(r.scheduled_at!);
        return t >= start && t <= end;
      })
      .map((r) => ({
        id: r.id,
        scheduled_at: r.scheduled_at!,
        label: `R${r.round}`,
      }));
  }, [interviewEvents, interviewCalMonth]);

  const nextInterviewSummary = useMemo(() => {
    const now = Date.now();
    const withSched = sortedInterviewEvents.filter((e) => e.scheduled_at);
    if (withSched.length === 0) return null;
    const upcoming = withSched.find((e) => new Date(e.scheduled_at!).getTime() >= now);
    return upcoming ?? withSched[withSched.length - 1]!;
  }, [sortedInterviewEvents]);

  /** Interviews tab: right-rail quick links (scheduling + related objects). */
  const interviewsTabQuickLinks = useMemo(() => {
    if (!app || !id) return [] as { key: string; to: string; label: string }[];
    return [
      {
        key: 'prep',
        to: `/interviews/prep?candidateId=${app.candidate_id}&jobId=${app.job_id}`,
        label: 'Prep & scorecards',
      },
      {
        key: 'ws',
        to: `/interviews/candidate/${app.candidate_id}?applicationId=${id}`,
        label: '[Interview Workspace]',
      },
      {
        key: 'cand',
        to: `/candidates/${app.candidate_id}`,
        label: app.candidate?.full_name ?? 'Candidate profile',
      },
      {
        key: 'job',
        to: `/jobs/${app.job_id}`,
        label: `${app.job?.title ?? 'Job'} @ ${app.client?.name ?? ''}`,
      },
      {
        key: 'mail',
        to: `/emails/compose?applicationId=${encodeURIComponent(id)}&returnTo=${encodeURIComponent(`/applications/${id}?tab=emails`)}`,
        label: 'Compose email',
      },
    ];
  }, [app, id]);

  const nextSteps = app ? (VALID_TRANSITIONS[app.status] ?? []) : [];
  const breadcrumbLabel = app ? [app.candidate?.full_name, app.job?.title].filter(Boolean).join(' — ') || 'Application' : 'Application';
  const clientName = app?.client?.name ?? 'Client';
  const clientHref = app?.client_id ? `/clients/${app.client_id}` : '/clients';
  const applicationsHref = app?.client_id ? `/clients/${app.client_id}?tab=applications` : '/applications';

  if (loading) return <FullPageSpinner />;
  if (!app) return <div className="vscode-text-secondary">Application not found.</div>;

  const isEditing = activeTab === 'overview' && searchParams.get('edit') === '1';

  const handleSaveScorecard = async () => {
    if (!id || !user?.orgId) return;
    setSavingScorecard(true);
    try {
      const payload: ScorecardCreate = {
        org_id: user.orgId,
        application_id: id,
        round: scorecardRound,
        overall_score: scorecardScore !== '' ? Number(scorecardScore) : null,
        recommendation: (scorecardRec || null) as ScorecardRecommendation | null,
        rubric: scorecardRubric,
        notes: scorecardNotes || null,
        interviewer_id: user.orgUserId ?? null,
        interviewer_name: user.name,
      };
      const created = await scorecardService.create(payload);
      setScorecards((prev) => [created, ...prev]);
      setScorecardFormOpen(false);
      setScorecardRubric(DEFAULT_RUBRIC.map((r) => ({ ...r })));
      setScorecardScore('');
      setScorecardRec('');
      setScorecardNotes('');
    } finally {
      setSavingScorecard(false);
    }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'emails', label: 'Emails' },
    { id: 'fitment', label: 'Fitment' },
    { id: 'interviews', label: `Interviews${interviewEvents.length > 0 ? ` (${interviewEvents.length})` : ''}` },
    { id: 'parameters', label: 'Parameters' },
  ];

  return (
    <div className="w-full min-w-0 space-y-0 mx-auto max-w-none">
      <RecordDetailLayout
        header={
          <RecordHeader
            avatarInitials={recordInitials(app.candidate?.full_name ?? '')}
            title={app.candidate?.full_name ?? 'Application'}
            meta={`${app.job?.title ?? '—'} @ ${app.client?.name ?? '—'}`}
            breadcrumb={[
              { label: clientName, href: clientHref },
              { label: 'Applications', href: applicationsHref },
              { label: breadcrumbLabel, href: `/applications/${id}` },
            ]}
            actionsEnd={
              <ListOperationsDropdown
                objectHistoryHref={objectHistoryUrl('applications')}
                onExport={(fmt, scope) => listExportNotImplemented('Application', fmt, scope)}
              />
            }
          />
        }
        metaRail={
          <RightRailWithAssistant
            recordId={app.id}
            entityKind="application"
            details={
              <RecordMetaCard
                title="Record"
                rows={[
                  { label: 'Created', value: formatDateTimeGmt(app.created_at) },
                  { label: 'Stage updated', value: formatDateTimeGmt(app.status_updated_at) },
                  { label: 'Owner', value: app.assigned_user?.display_name ?? 'Unassigned' },
                  { label: 'Client', value: app.client?.name ?? '—' },
                  { label: 'Job', value: app.job?.title ?? '—' },
                  { label: 'Fitment score', value: app.fitment_score != null ? String(app.fitment_score) : '—' },
                  { label: 'Last activity', value: formatDateTimeGmt(app.updated_at) },
                ]}
                footer={
                  <Link to={objectHistoryUrl('applications')} className="text-sm text-[var(--vscode-text-link)] hover:underline">
                    View history
                  </Link>
                }
              />
            }
          />
        }
        main={
          <>
      {statusConcurrencyMessage && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          <span>{statusConcurrencyMessage}</span>
          <button type="button" className="text-xs underline shrink-0" onClick={() => setStatusConcurrencyMessage(null)}>
            Dismiss
          </button>
        </div>
      )}

      {pipelineLockBootstrapError && (
        <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-3 text-sm vscode-text-secondary">
          {pipelineLockBootstrapError}
        </div>
      )}

      {pipelineLockedByPeer && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-700/50 bg-sky-950/35 px-4 py-3 text-sm text-sky-100">
          <span>
            <strong className="font-semibold">{pipelineLockedByPeer}</strong> is editing this application on the pipeline.
            Status moves and interview actions that change pipeline stage are disabled until their lock expires (~2 minutes) or they leave the screen.
          </span>
        </div>
      )}

      {/* Tabs */}
      <nav className="ggj-cand-tablist mb-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setTab(tab.id)}
            className={`ggj-cand-tab${activeTab === tab.id ? ' ggj-cand-tab--active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Context-specific toolbar */}
      <div className="ggj-profile-toolbar mb-5 flex-wrap gap-y-1">
        {activeTab === 'overview' && (
          <>
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('edit');
                      return next;
                    }, { replace: true });
                  }}
                  className={`${detailTbBtn} ${detailTbNeutral}`}
                >
                  <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                  <span>[Save]</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('edit');
                      return next;
                    }, { replace: true });
                    setInternalNotes(app?.internal_notes ?? '');
                    if (app?.placement_fee) setPlacementFee(String(app.placement_fee));
                  }}
                  className={`${detailTbBtn} ${detailTbDanger}`}
                >
                  <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                  <span>[Discard]</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('edit', '1');
                    return next;
                  }, { replace: true });
                }}
                className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700 inline-flex items-center gap-1.5`}
              >
                <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                <span className="text-slate-700 dark:text-gray-300">Edit</span>
              </button>
            )}
            <span className="vscode-text-tertiary text-xs select-none">·</span>
            <Link to={`/emails/compose?applicationId=${id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Send Email
            </Link>
          </>
        )}
        {activeTab === 'emails' && (
          <>
            <Link to={`/emails/compose?applicationId=${id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> New Email
            </Link>
            {app?.candidate?.email && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <a href={`mailto:${app.candidate.email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Email Candidate
                </a>
              </>
            )}
            {app?.client?.contact_email && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <a href={`mailto:${app.client.contact_email}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Email Client
                </a>
              </>
            )}
          </>
        )}
        {activeTab === 'fitment' && (
          <>
            {!optimization && (
              <Link to={`/single-optimize?applicationId=${id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                Optimize Resume
              </Link>
            )}
            {id && app && (
              <>
                <span className="vscode-text-tertiary text-xs select-none">·</span>
                <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5" onClick={() => setAiAssistField('screening')}>
                  AI Screening Summary
                </button>
              </>
            )}
          </>
        )}
        {activeTab === 'interviews' && app && id && (
          <>
            <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
              disabled={Boolean(pipelineLockedByPeer)}
              title={pipelineLockedByPeer ? 'Pipeline locked by another recruiter' : undefined}
              onClick={() => { setEditingEventId(null); setIntForm({ round: 1, scheduled_at: '', location: '', interviewer_name: '', interviewer_email: '', notes: '' }); setIntFormOpen(true); }}>
              <Plus className="w-3.5 h-3.5" /> Schedule Interview
            </button>
            <span className="vscode-text-tertiary text-xs select-none">·</span>
            <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
              onClick={() => { setScorecardFormOpen(true); setScorecardRubric(DEFAULT_RUBRIC.map((r) => ({ ...r }))); }}>
              <Plus className="w-3.5 h-3.5" /> Add Scorecard
            </button>
            <span className="vscode-text-tertiary text-xs select-none">·</span>
            <Link to={`/interviews/candidate/${app.candidate_id}?applicationId=${id}`}
              className="ggj-toolbar-btn inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Interview Workspace
            </Link>
          </>
        )}
        {activeTab === 'parameters' && (
          <button
            type="button"
            className={`${detailTbBtn} ${detailTbEdit}`}
            onClick={() => {
              setActiveTab('overview');
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('tab');
                next.set('edit', '1');
                return next;
              }, { replace: true });
            }}
          >
            <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-text-link)]" aria-hidden />
            <span>Edit</span>
          </button>
        )}
      </div>

      {/* ═══ TWO-COLUMN LAYOUT — Parameters tab is full width, others split ═══ */}
      <div className={`grid grid-cols-1 gap-6 ${activeTab !== 'parameters' ? 'lg:grid-cols-[1fr_300px]' : ''}`}>

        {/* ── LEFT: tab content ── */}
        <div className="min-w-0 space-y-4">

        {/* ═══════ OVERVIEW TAB ═══════ */}
        {activeTab === 'overview' && (
          <div className="space-y-4">

            <ApplicationDetailAttrGroup title="Application Status" icon={<Zap className="w-4 h-4" />}>
              {/* Status header with action */}
              <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <ApplicationStatusChip status={app.status} />
                  <span className="text-sm vscode-text-secondary">{STATUS_LABEL[app.status]}</span>
                </div>
                {isEditing && nextSteps.length > 0 && (
                  <select className="input text-sm max-w-[200px]" value=""
                    onChange={(e) => { const v = e.target.value as ApplicationStatus; if (v) handleStatusChange(v); }}
                    disabled={updatingStatus || Boolean(pipelineLockedByPeer)}
                    title={pipelineLockedByPeer ? 'Another recruiter is editing this application' : undefined}>
                    <option value="">Move to...</option>
                    {nextSteps.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                )}
              </div>

              {/* Stat cards row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg vscode-bg-tertiary p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-[10px] uppercase tracking-wide vscode-text-tertiary">Applied</span>
                  </div>
                  <p className="text-sm font-semibold vscode-text-primary">{formatDateOrDateTimeGmt(app.submitted_at)}</p>
                </div>
                <div className="rounded-lg vscode-bg-tertiary p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-[10px] uppercase tracking-wide vscode-text-tertiary">Assigned</span>
                  </div>
                  <p className="text-sm font-semibold vscode-text-primary truncate">{app.assigned_user?.display_name ?? app.assigned_user?.email ?? 'Unassigned'}</p>
                </div>
                <div className="rounded-lg vscode-bg-tertiary p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[10px] uppercase tracking-wide vscode-text-tertiary">Fitment</span>
                  </div>
                  <p className="text-sm font-semibold vscode-text-primary">{app.fitment_score != null ? `${app.fitment_score}%` : '—'}</p>
                </div>
                <div className="rounded-lg vscode-bg-tertiary p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-[10px] uppercase tracking-wide vscode-text-tertiary">ATS Score</span>
                  </div>
                  <p className="text-sm font-semibold vscode-text-primary">
                    {optimization?.ats_score_after != null ? (
                      <span className="text-emerald-400">{optimization.ats_score_after}</span>
                    ) : optimization?.ats_score_before != null ? (
                      optimization.ats_score_before
                    ) : '—'}
                  </p>
                </div>
              </div>

              {/* Details grid */}
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Pipeline stage">{STATUS_LABEL[app.status]}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Application date">{formatDateTimeGmt(app.submitted_at)}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Last updated">{app.updated_at ? formatDateTimeGmt(app.updated_at) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Assigned to">
                  {isEditing ? (
                    <select className="input text-sm w-full max-w-sm"
                      value={app.assigned_user?.id ?? ''}
                      onChange={(e) => handleAssign(e.target.value)}>
                      <option value="">Unassigned</option>
                      {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.display_name ?? m.email}</option>)}
                    </select>
                  ) : (app.assigned_user?.display_name ?? app.assigned_user?.email ?? '—')}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Source">{app.source ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Fitment score">{app.fitment_score != null ? `${app.fitment_score}` : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="ATS score (before)">{optimization?.ats_score_before ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="ATS score (after)">{optimization?.ats_score_after != null ? <span className="vscode-text-primary font-medium">{optimization.ats_score_after}</span> : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Masked PDF">{optimization?.masked_resume_url ? <span className="vscode-text-primary">Ready</span> : <span className="vscode-text-tertiary">Not generated</span>}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Interview rounds">{interviewEvents.length || '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Scorecards">{scorecards.length || '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Pipeline locked">{pipelineLockedByPeer ? <span className="vscode-text-secondary">{pipelineLockedByPeer}</span> : <span className="vscode-text-primary">Free</span>}</ApplicationDetailAttrRow>
                {app.placement_date && <ApplicationDetailAttrRow label="Placed">{formatDateOrDateTimeGmt(app.placement_date)}</ApplicationDetailAttrRow>}
                {app.placement_fee && (
                  <ApplicationDetailAttrRow label="Placement fee">
                    <span className="text-green-400 font-medium">₹{app.placement_fee.toLocaleString('en-IN')}</span>
                  </ApplicationDetailAttrRow>
                )}
              </dl>
              {(app.status === 'placed' || app.status === 'offer_accepted') && isEditing && (
                <div className="flex gap-2 items-center mt-3">
                  <span className="text-xs vscode-text-tertiary">Placement fee (₹)</span>
                  <input className="input max-w-[140px] text-sm" type="number" placeholder="45000"
                    value={placementFee} onChange={(e) => setPlacementFee(e.target.value)} />
                  <button className="ggj-toolbar-btn ggj-toolbar-btn--save text-xs" onClick={handleSaveFee} disabled={savingFee}>
                    {savingFee ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </button>
                </div>
              )}
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title="Candidate" icon={<User className="w-4 h-4" />}>
              {/* Candidate header card */}
              <div className="flex items-start gap-4 mb-4 pb-4 border-b vscode-border">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shrink-0"
                  style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                  {recordInitials(app.candidate?.full_name ?? '?')}
                </div>
                <div className="min-w-0 flex-1">
                  <Link to={`/candidates/${app.candidate_id}`} className="text-base font-semibold text-brand-400 hover:underline block truncate">
                    {app.candidate?.full_name ?? '—'}
                  </Link>
                  <p className="text-sm vscode-text-secondary truncate">{[app.candidate?.current_title, app.candidate?.current_company].filter(Boolean).join(' @ ') || 'No current role'}</p>
                  {app.candidate?.email && (
                    <a href={`mailto:${app.candidate.email}`} className="text-xs text-brand-400 hover:underline flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" /> {app.candidate.email}
                    </a>
                  )}
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-lg vscode-bg-tertiary p-2.5 text-center">
                  <p className="text-lg font-bold vscode-text-primary">{app.candidate?.years_experience ?? '—'}</p>
                  <p className="text-[10px] vscode-text-tertiary uppercase">Years exp</p>
                </div>
                <div className="rounded-lg vscode-bg-tertiary p-2.5 text-center">
                  <p className="text-lg font-bold vscode-text-primary">{app.candidate?.notice_period_days ?? '—'}</p>
                  <p className="text-[10px] vscode-text-tertiary uppercase">Notice (days)</p>
                </div>
                <div className="rounded-lg vscode-bg-tertiary p-2.5 text-center">
                  <p className="text-lg font-bold vscode-text-primary truncate">{app.candidate?.location ?? '—'}</p>
                  <p className="text-[10px] vscode-text-tertiary uppercase">Location</p>
                </div>
              </div>

              {/* Details grid */}
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Phone">{(app.candidate as { phone?: string })?.phone ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Highest education">{(app.candidate as { highest_education?: string })?.highest_education ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Current salary">{(app.candidate as { current_salary?: string })?.current_salary ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Expected salary">{app.candidate?.expected_salary ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Preferred work mode">{(app.candidate as { preferred_work_mode?: string })?.preferred_work_mode ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="LinkedIn">{(app.candidate as { linkedin_url?: string })?.linkedin_url
                  ? <a href={(app.candidate as { linkedin_url: string }).linkedin_url} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline truncate inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View profile</a>
                  : '—'}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Source">{app.source ?? '—'}</ApplicationDetailAttrRow>
              </dl>
              {app.candidate?.parsed_skills && app.candidate.parsed_skills.length > 0 && (
                <div className="mt-4 pt-4 border-t vscode-border">
                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-2 font-semibold">Skills ({app.candidate.parsed_skills.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {app.candidate.parsed_skills.slice(0, 8).map((s, idx) => (
                      <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ 
                          background: idx < 4 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                          color: idx < 4 ? '#10b981' : '#9ca3af',
                          border: `1px solid ${idx < 4 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`
                        }}>{s}</span>
                    ))}
                    {app.candidate.parsed_skills.length > 8 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium vscode-text-tertiary">+{app.candidate.parsed_skills.length - 8} more</span>
                    )}
                  </div>
                </div>
              )}
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title="Role & Client">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Job title">
                  <Link to={`/jobs/${app.job_id}`} className="text-brand-400 hover:underline font-medium">{app.job?.title ?? '—'}</Link>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job status"><span className="capitalize">{app.job?.status ?? '—'}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Department">{app.job?.department ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Location">{app.job?.location ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Employment type">{app.job?.employment_type ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Salary range">{app.job?.salary_range ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Experience req.">{app.job?.experience_required ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="No. of openings">{(app.job as { openings?: number })?.openings ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client">
                  <Link to={`/clients/${app.client_id}`} className="text-brand-400 hover:underline">{app.client?.name ?? '—'}</Link>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client industry">{app.client?.industry ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client website">{(app.client as { website?: string })?.website ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client contact">{app.client?.contact_name ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client email">
                  {app.client?.contact_email
                    ? <a href={`mailto:${app.client.contact_email}`} className="text-brand-400 hover:underline">{app.client.contact_email}</a>
                    : '—'}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client phone">{(app.client as { contact_phone?: string })?.contact_phone ?? '—'}</ApplicationDetailAttrRow>
              </dl>
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title={
              <span className="inline-flex items-center gap-2">
                Internal Notes
                {id && <AiAssistIconButton onClick={() => setAiAssistField('internal')} title="AI assist — internal notes" />}
              </span> as unknown as string
            }>
              <textarea className="input w-full resize-none text-sm" rows={3}
                placeholder="Notes visible only to your team..."
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                onBlur={async () => {
                  if (!id || !isEditing || internalNotes === (app?.internal_notes ?? '')) return;
                  setSavingNotes(true);
                  await applicationService.updateNotes(id, internalNotes);
                  setApp((a) => (a ? { ...a, internal_notes: internalNotes || null } : a));
                  setSavingNotes(false);
                }}
                disabled={!isEditing}
              />
              {savingNotes && <p className="text-xs vscode-text-secondary mt-1">Saving...</p>}
            </ApplicationDetailAttrGroup>

            {OFFER_STAGE_STATUSES.includes(app.status) && (
              <ApplicationDetailAttrGroup title={
                <span className="inline-flex items-center gap-2">
                  Offer & Compensation (internal)
                  {id && <AiAssistIconButton onClick={() => setAiAssistField('offer')} title="AI assist — offer draft notes" />}
                </span> as unknown as string
              }>
                <textarea className="input w-full resize-none text-sm" rows={4}
                  placeholder="Internal offer talking points, comp band, start date targets..."
                  value={offerNotes}
                  onChange={(e) => setOfferNotes(e.target.value)}
                  onBlur={async () => {
                    if (!id || !isEditing || offerNotes === (app?.offer_notes ?? '')) return;
                    setSavingOfferNotes(true);
                    await applicationService.updateOfferNotes(id, offerNotes);
                    setApp((a) => (a ? { ...a, offer_notes: offerNotes || null } : a));
                    setSavingOfferNotes(false);
                  }}
                  disabled={!isEditing}
                />
                {savingOfferNotes && <p className="text-xs vscode-text-secondary mt-1">Saving...</p>}
              </ApplicationDetailAttrGroup>
            )}

            <ApplicationDetailAttrGroup title="Client-safe Resume (Masked)">
              <div className="space-y-3">
                {optimization?.masked_resume_url ? (
                  <div className="space-y-2">
                    <dl className="ggj-attr-grid text-sm">
                      <ApplicationDetailAttrRow label="Candidate">{app.candidate?.full_name ?? '—'}</ApplicationDetailAttrRow>
                      {optimization.watermark_ref && <ApplicationDetailAttrRow label="Client ref"><span className="font-mono text-xs">{optimization.watermark_ref}</span></ApplicationDetailAttrRow>}
                      {optimization.alias_used && <ApplicationDetailAttrRow label="Alias used">{optimization.alias_used}</ApplicationDetailAttrRow>}
                    </dl>
                    <div className="flex gap-2 flex-wrap mt-2">
                      <button type="button" onClick={handlePreviewMaskedResume} disabled={loadingMaskedPreview}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-xs font-medium disabled:opacity-60">
                        {loadingMaskedPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview (masked)
                      </button>
                      {applicationResumeStoragePath(app, optimization) && (
                        <button type="button" onClick={() => void handlePreviewFullResume()} disabled={loadingFullPreview}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-500/50 bg-brand-500/10 text-brand-200 hover:bg-brand-500/20 text-xs font-medium disabled:opacity-60">
                          {loadingFullPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview (full)
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button type="button" onClick={handleGeneratePdf} disabled={generatingPdf} className="btn-secondary text-xs">
                      {generatingPdf ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</> : <><FileText className="w-3 h-3" /> Generate Masked PDF</>}
                    </button>
                    <p className="text-xs vscode-text-secondary">Builds from the candidate's saved resume. Contact fields are masked per your template.</p>
                    {applicationResumeStoragePath(app, optimization) && (
                      <button type="button" onClick={() => void handlePreviewFullResume()} disabled={loadingFullPreview}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-500/50 bg-brand-500/10 text-brand-200 hover:bg-brand-500/20 text-xs font-medium disabled:opacity-60">
                        {loadingFullPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview original
                      </button>
                    )}
                  </div>
                )}
              </div>
            </ApplicationDetailAttrGroup>
          </div>
        )}

        {/* ═══════ EMAILS TAB ═══════ */}
        {activeTab === 'emails' && (() => {
          const rootThreads = emailThreads.filter(t => !t.in_reply_to_thread_id);
          const repliesMap: Record<string, EmailThread[]> = {};
          emailThreads.filter(t => t.in_reply_to_thread_id).forEach(t => {
            if (!repliesMap[t.in_reply_to_thread_id!]) repliesMap[t.in_reply_to_thread_id!] = [];
            repliesMap[t.in_reply_to_thread_id!].push(t);
          });
          const displayList = rootThreads.length > 0 ? rootThreads : emailThreads;

          return (
            <div className="space-y-4">

              {/* ── Multi-value list style — one label, stacked clickable values ── */}
              <ApplicationDetailAttrGroup title="Email Threads">
                {emailThreads.length === 0 ? (
                  <div className="py-6 text-center space-y-3">
                    <MailOpen className="w-9 h-9 vscode-text-tertiary mx-auto" />
                    <p className="vscode-text-secondary text-sm">No emails for this application yet.</p>
                    <Link to={`/emails/compose?applicationId=${id}`} className="btn-primary text-sm inline-flex items-center gap-1.5">
                      <Mail className="w-4 h-4" /> Compose First Email
                    </Link>
                  </div>
                ) : (
                  <dl className="ggj-attr-grid text-sm">
                    {/* Header row */}
                    <dt className="py-2 text-xs vscode-text-tertiary font-medium leading-5 pr-2">Threads</dt>
                    <dd className="py-2 text-sm vscode-text-primary leading-5">
                      <div className="space-y-1.5">
                        {displayList.map((thread) => {
                          const replies = repliesMap[thread.id] ?? [];
                          const isResponded = !!thread.cta_response;
                          const isAwaiting  = !isResponded && !!thread.sent_at;
                          return (
                            <button key={thread.id} type="button"
                              onClick={() => { setEmailDetailThread(thread); setEmailAccordionOpen({ body: true, cta: false, track: false, replies: replies.length > 0 }); }}
                              className="flex items-center gap-2 text-brand-400 hover:text-brand-300 hover:underline text-sm text-left group w-full">
                              <span className="truncate">{thread.subject || '(No subject)'}</span>
                              {isAwaiting && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0 animate-pulse" />}
                              {isResponded && <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />}
                              {replies.length > 0 && <span className="text-[10px] vscode-text-tertiary shrink-0">(+{replies.length})</span>}
                            </button>
                          );
                        })}
                      </div>
                    </dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Candidate</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      {app.candidate?.email ? <a href={`mailto:${app.candidate.email}`} className="text-brand-400 hover:underline">{app.candidate.email}</a> : '—'}
                    </dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Client contact</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      {app.client?.contact_email ? <a href={`mailto:${app.client.contact_email}`} className="text-brand-400 hover:underline">{app.client.contact_email}</a> : '—'}
                    </dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Sent</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{emailThreads.length}</dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Responded</dt>
                    <dd className="vscode-text-primary min-w-0 break-words font-medium">{emailThreads.filter(t => t.cta_response).length}</dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Awaiting reply</dt>
                    <dd className="min-w-0 break-words font-medium" style={{ color: 'var(--vscode-text-link)' }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Total opens</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Last sent</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      {emailThreads.length > 0
                        ? formatDateTimeGmt(new Date(Math.max(...emailThreads.map(t => new Date(t.sent_at ?? 0).getTime()))).toISOString())
                        : '—'}
                    </dd>

                    <dt className="vscode-text-secondary pr-2 min-w-0">Assigned to</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{app.assigned_user?.display_name ?? app.assigned_user?.email ?? '—'}</dd>
                  </dl>
                )}
              </ApplicationDetailAttrGroup>

              {emailThreads.length > 0 && (
                <ApplicationDetailAttrGroup title="Email Engagement Summary">
                  <dl className="ggj-attr-grid text-sm">
                    <ApplicationDetailAttrRow label="Total threads">{emailThreads.length}</ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Responded"><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.cta_response).length}</span></ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Awaiting reply"><span className="font-medium" style={{ color: 'var(--vscode-text-link)' }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</span></ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Total opens">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="With replies">{emailThreads.filter(t => t.in_reply_to_thread_id).length}</ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Response rate">
                      {emailThreads.length > 0
                        ? `${Math.round((emailThreads.filter(t => t.cta_response).length / emailThreads.length) * 100)}%`
                        : '—'}
                    </ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Candidate email">
                      {app.candidate?.email ? <a href={`mailto:${app.candidate.email}`} className="text-brand-400 hover:underline">{app.candidate.email}</a> : '—'}
                    </ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Client contact">
                      {app.client?.contact_email ? <a href={`mailto:${app.client.contact_email}`} className="text-brand-400 hover:underline">{app.client.contact_email}</a> : '—'}
                    </ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="First sent">
                      {emailThreads.length > 0
                        ? formatDateTimeGmt(new Date(Math.min(...emailThreads.map(t => new Date(t.sent_at ?? Date.now()).getTime()))).toISOString())
                        : '—'}
                    </ApplicationDetailAttrRow>
                    <ApplicationDetailAttrRow label="Last sent">
                      {emailThreads.length > 0
                        ? formatDateTimeGmt(new Date(Math.max(...emailThreads.map(t => new Date(t.sent_at ?? 0).getTime()))).toISOString())
                        : '—'}
                    </ApplicationDetailAttrRow>
                  </dl>
                </ApplicationDetailAttrGroup>
              )}
            </div>
          );
        })()}

        {/* ═══════ FITMENT TAB ═══════ */}
        {activeTab === 'fitment' && (
          <div className="space-y-4">
            <ApplicationDetailAttrGroup title="ATS & Optimization Scores">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="ATS score (before)">{optimization?.ats_score_before != null ? String(optimization.ats_score_before) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="ATS score (after)">
                  {optimization?.ats_score_after != null
                    ? <span className="vscode-text-primary font-medium">{optimization.ats_score_after}</span>
                    : '—'}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Score delta">
                  {optimization?.ats_score_before != null && optimization?.ats_score_after != null
                    ? <span className="vscode-text-primary font-medium">
                        {optimization.ats_score_after > optimization.ats_score_before ? '+' : ''}{optimization.ats_score_after - optimization.ats_score_before}
                      </span>
                    : '—'}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Fitment score">{app.fitment_score != null ? String(app.fitment_score) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Masked resume">
                  {optimization?.masked_resume_url
                    ? <span className="vscode-text-primary">Generated</span>
                    : <span className="vscode-text-tertiary">Not generated</span>}
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Alias used">{optimization?.alias_used ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Watermark ref"><span className="font-mono text-xs">{optimization?.watermark_ref ?? '—'}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Optimized on">{optimization?.created_at ? formatDateTimeGmt(optimization.created_at) : '—'}</ApplicationDetailAttrRow>
              </dl>
              {!optimization && (
                <div className="mt-3 space-y-2">
                  <Link to={`/single-optimize?applicationId=${id}`} className="btn-secondary text-sm inline-flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> Optimize Resume
                  </Link>
                  <p className="text-xs vscode-text-secondary">Run resume optimization to generate ATS scores and a masked client-safe PDF.</p>
                </div>
              )}
              {optimization?.masked_resume_url && (
                <div className="flex gap-2 flex-wrap mt-3">
                  <button type="button" onClick={handlePreviewMaskedResume} disabled={loadingMaskedPreview}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white text-xs font-medium disabled:opacity-60">
                    {loadingMaskedPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview masked
                  </button>
                  {applicationResumeStoragePath(app, optimization) && (
                    <button type="button" onClick={() => void handlePreviewFullResume()} disabled={loadingFullPreview}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-500/50 bg-brand-500/10 text-brand-200 hover:bg-brand-500/20 text-xs font-medium disabled:opacity-60">
                      {loadingFullPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview original
                    </button>
                  )}
                </div>
              )}
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title="Candidate vs. Job Match">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Candidate">{app.candidate?.full_name ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job title">{app.job?.title ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Department">{app.job?.department ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Location match">{app.candidate?.location && app.job?.location ? (app.candidate.location === app.job.location ? <span className="text-green-400">Match</span> : <span className="text-amber-400">Partial</span>) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Experience req.">{app.job?.experience_required ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Candidate exp.">{app.candidate?.years_experience != null ? `${app.candidate.years_experience} yrs` : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Salary range">{app.job?.salary_range ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Expected salary">{app.candidate?.expected_salary ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Notice period">{app.candidate?.notice_period_days != null ? `${app.candidate.notice_period_days} days` : '—'}</ApplicationDetailAttrRow>
              </dl>
              {app.candidate?.parsed_skills && app.candidate.parsed_skills.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Candidate Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {app.candidate.parsed_skills.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ background: 'var(--ggj-accent-tint)', color: 'var(--vscode-text-link)', border: '1px solid var(--ggj-accent-border)' }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </ApplicationDetailAttrGroup>

            {app.fitment_json && (
              <ApplicationDetailAttrGroup title="Fitment Details">
                <p className="text-xs vscode-text-secondary mb-2">Detailed fitment analysis from the optimization engine.</p>
                <dl className="ggj-attr-grid text-sm">
                  {Object.entries(app.fitment_json as Record<string, unknown>).slice(0, 14).map(([k, v]) => (
                    <ApplicationDetailAttrRow key={k} label={k}>{String(v ?? '—')}</ApplicationDetailAttrRow>
                  ))}
                </dl>
              </ApplicationDetailAttrGroup>
            )}

            {!app.fitment_json && optimization && (
              <ApplicationDetailAttrGroup title="Fitment Details">
                <p className="text-sm vscode-text-secondary py-2">Full skill gaps and matched/missing skills will appear here when the fitment analysis is persisted.</p>
              </ApplicationDetailAttrGroup>
            )}
          </div>
        )}

        {/* ═══════ INTERVIEWS TAB ═══════ */}
        {activeTab === 'interviews' && app && id && (() => {
          const nowLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          const isPastDate = intForm.scheduled_at && intForm.scheduled_at < nowLocal;
          return (
          <div className="space-y-4">

            {/* ── Calendar on left, form slides in on right when day is clicked ── */}
            <div className={`grid gap-4 items-start ${intFormOpen ? 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_460px]' : 'grid-cols-1'}`}>
              {/* Calendar */}
              <div>
                <p className="text-xs vscode-text-tertiary mb-2">Click a future day to schedule. Dots indicate booked rounds.</p>
                <InterviewMonthCalendar
                  monthAnchor={interviewCalMonth} onMonthAnchorChange={setInterviewCalMonth}
                  events={interviewMonthCalendarEvents} selectedId={interviewCalSelectedEventId}
                  onSelectEvent={(evId) => {
                    setInterviewCalSelectedEventId(evId);
                    const ev = interviewEvents.find((e) => e.id === evId);
                    if (ev) openEditEvent(ev);
                  }}
                  onDayClick={(day) => {
                    setEditingEventId(null);
                    setIntForm((f) => ({ ...f, scheduled_at: dayToDatetimeLocalSeed(day) }));
                    setIntFormOpen(true);
                  }}
                />
              </div>

              {/* Form — slides in to the right of the calendar */}
              {intFormOpen && (() => {
                const INTERVIEW_TYPES = [
                  { value: 'screening',    label: 'Screening Call',         desc: 'Initial HR / recruiter call to assess fit' },
                  { value: 'technical',    label: 'Technical Interview',    desc: 'Coding, system design or domain depth' },
                  { value: 'panel',        label: 'Panel Interview',        desc: 'Multiple interviewers, cross-functional' },
                  { value: 'managerial',   label: 'Managerial Round',       desc: 'With hiring manager or team lead' },
                  { value: 'hr_final',     label: 'HR / Final Round',       desc: 'Culture fit & offer discussion' },
                  { value: 'case_study',   label: 'Case Study',             desc: 'Take-home or live practical assignment' },
                  { value: 'onsite',       label: 'On-site',                desc: 'Office-based in-person interview' },
                ] as const;
                type IntType = typeof INTERVIEW_TYPES[number]['value'];
                const selType = ((intForm as { interviewType?: IntType }).interviewType ?? 'screening') as IntType;
                const selTypeMeta = INTERVIEW_TYPES.find(t => t.value === selType) ?? INTERVIEW_TYPES[0];

                const ROUND_META = [
                  { r: 1, label: 'Screen',  color: '#6366f1', desc: 'Initial fit check' },
                  { r: 2, label: 'Tech',    color: '#0ea5e9', desc: 'Skill assessment' },
                  { r: 3, label: 'Panel',   color: '#8b5cf6', desc: 'Cross-functional' },
                  { r: 4, label: 'Final',   color: '#10b981', desc: 'Offer / culture' },
                ] as const;

                return (
                  <div className="rounded-2xl border overflow-hidden shadow-xl flex flex-col"
                    style={{
                      background: 'var(--vscode-bg-primary)',
                      borderColor: 'var(--ggj-accent-border)',
                      maxHeight: '82vh',
                      boxShadow: '0 8px 32px color-mix(in srgb, var(--vscode-text-link) 12%, transparent), 0 2px 8px #00000030',
                    }}>

                    {/* ── Header ── */}
                    <div className="shrink-0 px-5 py-3.5 border-b flex items-center justify-between"
                      style={{
                        background: 'linear-gradient(135deg, color-mix(in srgb, var(--vscode-text-link) 16%, var(--vscode-bg-elevated)) 0%, color-mix(in srgb, var(--vscode-text-link) 5%, var(--vscode-bg-elevated)) 100%)',
                        borderBottomColor: 'color-mix(in srgb, var(--vscode-text-link) 22%, var(--vscode-border-primary))',
                      }}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: 'color-mix(in srgb, var(--vscode-text-link) 20%, var(--vscode-bg-elevated))', border: '1.5px solid var(--ggj-accent-border)' }}>
                          {editingEventId ? <Edit2 className="w-4 h-4" style={{ color: 'var(--vscode-text-link)' }} /> : <CalendarPlus className="w-4 h-4" style={{ color: 'var(--vscode-text-link)' }} />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold vscode-text-primary leading-tight">
                            {editingEventId ? `Edit Round ${intForm.round} Interview` : 'Schedule Interview'}
                          </p>
                          <p className="text-[11px] vscode-text-tertiary truncate mt-0.5">
                            {app.candidate?.full_name}
                            {app.client?.name ? <> · <span style={{ color: 'var(--vscode-text-link)' }}>{app.client.name}</span></> : null}
                          </p>
                        </div>
                      </div>
                      <button type="button" onClick={closeIntForm}
                        className="p-1.5 rounded-lg transition-colors shrink-0"
                        style={{ color: 'var(--vscode-text-tertiary)' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--vscode-text-link) 14%, var(--vscode-bg-elevated))')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* ── Scrollable body ── */}
                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

                      {/* Round (Stage) selector */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary mb-2 font-bold">Interview Round</p>
                        <div className="grid grid-cols-4 gap-2">
                          {ROUND_META.map(({ r, label, color, desc }) => {
                            const active = intForm.round === r;
                            return (
                              <button key={r} type="button"
                                onClick={() => setIntForm((f) => ({ ...f, round: r }))}
                                className="flex flex-col items-center py-2.5 px-1 rounded-xl border text-xs font-medium transition-all"
                                style={active
                                  ? { background: `${color}20`, borderColor: color, color }
                                  : { background: 'var(--vscode-bg-secondary)', borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                                <span className="text-base font-extrabold leading-none mb-0.5"
                                  style={active ? { color } : {}}>R{r}</span>
                                <span className="font-semibold text-[10px]">{label}</span>
                                <span className="text-[9px] opacity-70 mt-0.5 leading-tight text-center">{desc}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Interview Type */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary mb-2 font-bold">Interview Type</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {INTERVIEW_TYPES.map(t => {
                            const active = selType === t.value;
                            return (
                              <button key={t.value} type="button"
                                onClick={() => setIntForm((f) => ({ ...f, interviewType: t.value }))}
                                className="text-left rounded-xl p-2.5 border text-xs transition-all"
                                style={active
                                  ? { background: 'color-mix(in srgb, var(--vscode-text-link) 14%, var(--vscode-bg-elevated))', borderColor: 'color-mix(in srgb, var(--vscode-text-link) 45%, var(--vscode-border-primary))', color: 'var(--vscode-text-primary)' }
                                  : { background: 'var(--vscode-bg-secondary)', borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                                <p className="font-semibold leading-tight">{t.label}</p>
                                <p className="text-[9px] mt-0.5 opacity-70 leading-tight">{t.desc}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t" style={{ borderColor: 'color-mix(in srgb, var(--vscode-text-link) 14%, var(--vscode-border-primary))' }} />

                      {/* Date + Duration */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary mb-2 font-bold flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" /> Date & Time
                        </p>
                        <div className="flex gap-2">
                          <input type="datetime-local" className="input flex-1 text-xs"
                            min={nowLocal}
                            value={intForm.scheduled_at}
                            onChange={(e) => setIntForm((f) => ({ ...f, scheduled_at: e.target.value }))} />
                          <select className="input w-[88px] text-xs shrink-0"
                            value={(intForm as { duration?: number }).duration ?? 60}
                            onChange={(e) => setIntForm((f) => ({ ...f, duration: Number(e.target.value) }))}>
                            <option value={30}>30 min</option>
                            <option value={45}>45 min</option>
                            <option value={60}>1 hr</option>
                            <option value={90}>1.5 hr</option>
                            <option value={120}>2 hr</option>
                          </select>
                        </div>
                        {isPastDate && (
                          <div className="flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg text-xs"
                            style={{ background: '#ef444415', color: '#f87171', border: '1px solid #ef444425' }}>
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            <span>Cannot schedule in the past. Please pick a future date.</span>
                          </div>
                        )}
                      </div>

                      {/* Location */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary mb-2 font-bold flex items-center gap-1.5">
                          <Video className="w-3 h-3" /> Location / Link
                        </p>
                        <input className="input w-full text-xs" value={intForm.location}
                          onChange={(e) => setIntForm((f) => ({ ...f, location: e.target.value }))}
                          placeholder="Paste Meet / Zoom / Teams link — or physical address" />
                      </div>

                      {/* Interviewer */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary mb-2 font-bold flex items-center gap-1.5">
                          <User className="w-3 h-3" /> Interviewer
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <input className="input text-xs" placeholder="Full name"
                            value={intForm.interviewer_name}
                            onChange={(e) => setIntForm((f) => ({ ...f, interviewer_name: e.target.value }))} />
                          <input type="email" className="input text-xs" placeholder="Work email"
                            value={intForm.interviewer_email}
                            onChange={(e) => setIntForm((f) => ({ ...f, interviewer_email: e.target.value }))} />
                        </div>
                      </div>

                      {/* Agenda / Notes — inline AI suggestion, no popup */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] uppercase tracking-widest vscode-text-tertiary font-bold">Agenda / Notes</p>
                          <button type="button"
                            onClick={() => {
                              const suggestions: Record<string, string> = {
                                screening:   `• Introduction and candidate background\n• Role overview and expectations\n• Availability and notice period\n• Salary expectations`,
                                technical:   `• Technical screening: ${app.job?.title ?? 'role'}-specific questions\n• Coding / system design exercise\n• Past project deep-dive\n• Q&A`,
                                panel:       `• Cross-functional fit assessment\n• Scenario-based questions\n• Team collaboration style\n• Culture and values alignment`,
                                managerial:  `• Leadership and decision-making style\n• Conflict resolution examples\n• Team management experience\n• Vision alignment`,
                                hr_final:    `• Offer discussion and compensation\n• Start date and onboarding\n• Benefits overview\n• Final Q&A`,
                                case_study:  `• Problem statement walkthrough\n• Approach and methodology discussion\n• Presentation of findings\n• Follow-up questions`,
                                onsite:      `• Office tour and team introductions\n• Final technical round\n• Culture fit conversation\n• Next steps discussion`,
                              };
                              const suggestion = suggestions[selType] ?? `• Key topics for ${selTypeMeta.label}`;
                              setIntForm((f) => ({ ...f, notes: f.notes ? `${f.notes}\n\n${suggestion}` : suggestion }));
                            }}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all font-medium"
                            style={{ background: 'color-mix(in srgb, var(--vscode-text-link) 12%, var(--vscode-bg-elevated))', color: 'var(--vscode-text-link)', border: '1px solid var(--ggj-accent-border)' }}>
                            <Sparkles className="w-3 h-3" />
                            Suggest agenda
                          </button>
                        </div>
                        <textarea className="input w-full resize-none text-xs leading-relaxed" rows={4}
                          value={intForm.notes}
                          placeholder={`Key topics and agenda for ${selTypeMeta.label}…`}
                          onChange={(e) => setIntForm((f) => ({ ...f, notes: e.target.value }))} />
                      </div>
                    </div>

                    {/* ── Footer ── */}
                    <div className="shrink-0 border-t" style={{ background: 'var(--vscode-bg-secondary)', borderTopColor: 'color-mix(in srgb, var(--vscode-text-link) 18%, var(--vscode-border-primary))' }}>
                      {/* Resend invite toggle (edit mode only) */}
                      {editingEventId && (
                        <div className="flex items-center gap-2 px-5 pt-3 pb-1">
                          <button type="button"
                            onClick={() => setResendInviteOnEdit(v => !v)}
                            className={`w-8 h-4 rounded-full transition-colors flex items-center ${resendInviteOnEdit ? 'justify-end' : 'justify-start'}`}
                            style={{ background: resendInviteOnEdit ? 'var(--vscode-text-link)' : 'var(--vscode-bg-tertiary)', padding: '2px' }}>
                            <span className="w-3 h-3 rounded-full bg-white block" />
                          </button>
                          <span className="text-[10px] vscode-text-secondary flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            Resend invite to candidate & interviewer after saving
                          </span>
                        </div>
                      )}
                      {!editingEventId && (
                        <div className="flex items-center gap-1.5 px-5 pt-3 pb-1 text-[10px] vscode-text-tertiary">
                          <Send className="w-3 h-3" />
                          Invite sent to candidate & interviewer on save
                        </div>
                      )}
                      <div className="flex items-center justify-between px-5 pb-3 pt-2">
                        <div className="flex gap-2">
                          <button className="btn-secondary text-xs py-1.5 px-3" onClick={closeIntForm}>Cancel</button>
                          {editingEventId && (
                            <button type="button"
                              className="text-xs py-1.5 px-3 rounded-lg flex items-center gap-1.5 transition-colors"
                              style={{ background: '#ef444415', color: '#f87171', border: '1px solid #ef444430' }}
                              disabled={deletingEventId === editingEventId || Boolean(pipelineLockedByPeer)}
                              onClick={() => {
                                if (!editingEventId || pipelineLockedByPeer) return;
                                setDeleteInterviewRoundOpen(true);
                              }}>
                              {deletingEventId === editingEventId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                              Delete
                            </button>
                          )}
                        </div>
                        <button className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5"
                          disabled={savingInt || !intForm.scheduled_at || Boolean(isPastDate) || Boolean(pipelineLockedByPeer)}
                          onClick={async () => {
                            if (!app || !user?.orgId || !id) return;
                            if (pipelineLockedByPeer || isPastDate) return;
                            setSavingInt(true);
                            try {
                              if (editingEventId) {
                                // UPDATE existing event
                                await interviewEventService.update(editingEventId, {
                                  round: intForm.round,
                                  scheduled_at: new Date(intForm.scheduled_at).toISOString(),
                                  location: intForm.location || null,
                                  interviewer_name: intForm.interviewer_name || null,
                                  interviewer_email: intForm.interviewer_email || null,
                                  notes: intForm.notes || null,
                                  duration_minutes: (intForm as { duration?: number }).duration ?? 60,
                                });
                                setInterviewEvents((prev) => prev.map((e) => e.id === editingEventId ? {
                                  ...e,
                                  round: intForm.round,
                                  scheduled_at: new Date(intForm.scheduled_at).toISOString(),
                                  location: intForm.location || null,
                                  interviewer_name: intForm.interviewer_name || null,
                                  interviewer_email: intForm.interviewer_email || null,
                                  notes: intForm.notes || null,
                                  duration_minutes: (intForm as { duration?: number }).duration ?? 60,
                                } : e).sort((a, b) => interviewSortTime(a) - interviewSortTime(b)));
                                if (resendInviteOnEdit) {
                                  try {
                                    await interviewEventService.enqueueInterviewConfirmationEmail({
                                      org_id: user.orgId,
                                      application_id: id,
                                      scheduled_at: new Date(intForm.scheduled_at).toISOString(),
                                      round: intForm.round,
                                      location: intForm.location || null,
                                      candidateName: app.candidate?.full_name,
                                      candidateEmail: app.candidate?.email ?? null,
                                      jobTitle: app.job?.title,
                                      clientName: app.client?.name,
                                      recruiterName: user.name,
                                      orgName: user.orgName ?? undefined,
                                      interviewerEmail: intForm.interviewer_email || null,
                                      interviewerName: intForm.interviewer_name || null,
                                      createdBy: user.name,
                                    });
                                  } catch (emailErr) {
                                    console.warn('[interview edit] Could not resend invite:', emailErr);
                                  }
                                }
                                closeIntForm();
                              } else {
                                // CREATE new event
                                const created = await interviewEventService.create(
                                  { org_id: user.orgId, application_id: id, round: intForm.round, scheduled_at: new Date(intForm.scheduled_at).toISOString(), location: intForm.location || null, interviewer_name: intForm.interviewer_name || null, interviewer_email: intForm.interviewer_email || null, notes: intForm.notes || null, duration_minutes: (intForm as { duration?: number }).duration ?? 60 },
                                  { candidateName: app.candidate?.full_name, candidateEmail: app.candidate?.email ?? null, jobTitle: app.job?.title, clientName: app.client?.name, recruiterName: user.name, orgName: user.orgName ?? undefined, createdBy: user.name }
                                );
                                const schedStatus: ApplicationStatus = intForm.round >= 2 ? 'interview_2_scheduled' : 'interview_1_scheduled';
                                const { updated_at } = await applicationService.updateStatus(id, schedStatus, `Interview round ${intForm.round} scheduled`, undefined, undefined, { expectedUpdatedAt: app.updated_at });
                                setApp((prev) => (prev ? { ...prev, status: schedStatus, updated_at } : prev));
                                setInterviewEvents((prev) => [...prev, created].sort((a, b) => interviewSortTime(a) - interviewSortTime(b)));
                                setIntForm({ round: 1, scheduled_at: '', location: '', interviewer_name: '', interviewer_email: '', notes: '' });
                                closeIntForm();
                              }
                            } catch (e) {
                              if (isApplicationStatusConflictError(e)) { setStatusConcurrencyMessage(e.message); const fresh = await applicationService.getById(id); setApp(fresh); }
                              else { console.error(e); setStatusConcurrencyMessage(e instanceof Error ? e.message : 'Could not save.'); }
                            } finally { setSavingInt(false); }
                          }}>
                          {savingInt
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                            : editingEventId
                              ? <><Edit2 className="w-3.5 h-3.5" /> Save Changes</>
                              : <><CalendarPlus className="w-3.5 h-3.5" /> Save & Send Invite</>}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── SCORECARD SECTION (merged here, after interview rounds) ── */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 border-t vscode-border" />
              <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary font-semibold shrink-0">Scorecards</p>
              <div className="flex-1 border-t vscode-border" />
            </div>

            {scorecardFormOpen && (
              <ApplicationDetailAttrGroup title="New Scorecard">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="label mb-1">Round</label><input className="input w-full" value={scorecardRound} onChange={(e) => setScorecardRound(e.target.value)} placeholder="Round 1" /></div>
                  <div><label className="label mb-1">Overall score (1–5)</label>
                    <select className="input w-full" value={scorecardScore} onChange={(e) => setScorecardScore(e.target.value === '' ? '' : Number(e.target.value))}>
                      <option value="">— none —</option>{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div><label className="label mb-1">Recommendation</label>
                    <select className="input w-full" value={scorecardRec} onChange={(e) => setScorecardRec(e.target.value as ScorecardRecommendation | '')}>
                      <option value="">— none —</option>
                      {(Object.entries(RECOMMENDATION_LABELS) as [ScorecardRecommendation, string][]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="label mb-2">Rubric</label>
                  <div className="space-y-2">
                    {scorecardRubric.map((item, i) => (
                      <div key={i} className="grid grid-cols-[1fr_80px_1fr] gap-2 items-center">
                        <span className="text-xs vscode-text-secondary">{item.criterion}</span>
                        <select className="input text-xs py-1" value={item.score ?? ''} onChange={(e) => setScorecardRubric((prev) => prev.map((r, j) => j === i ? { ...r, score: e.target.value === '' ? null : Number(e.target.value) } : r))}>
                          <option value="">—</option>{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <input className="input text-xs py-1" placeholder="note" value={item.note} onChange={(e) => setScorecardRubric((prev) => prev.map((r, j) => j === i ? { ...r, note: e.target.value } : r))} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <label className="label mb-0">Notes</label>
                    <AiAssistIconButton onClick={() => setAiAssistField('scorecard')} title="AI assist — scorecard notes" />
                  </div>
                  <textarea className="input w-full" rows={3} value={scorecardNotes} onChange={(e) => setScorecardNotes(e.target.value)} placeholder="Overall impression, key observations…" />
                </div>
                <div className="flex gap-2 mt-3">
                  <button className="btn-primary" onClick={handleSaveScorecard} disabled={savingScorecard}>{savingScorecard ? 'Saving…' : 'Save scorecard'}</button>
                  <button className="btn-secondary" onClick={() => setScorecardFormOpen(false)}>Cancel</button>
                </div>
              </ApplicationDetailAttrGroup>
            )}

            {scorecards.length === 0 && !scorecardFormOpen && (
              <ApplicationDetailAttrGroup title="Interview Scorecards">
                <div className="py-6 text-center"><p className="vscode-text-secondary text-sm">No scorecards yet. Click Add Scorecard to start.</p></div>
              </ApplicationDetailAttrGroup>
            )}
            {scorecards.map((sc) => (
              <ApplicationDetailAttrGroup key={sc.id} title={`${sc.round} Scorecard`}>
                <dl className="ggj-attr-grid text-sm mb-3">
                  <ApplicationDetailAttrRow label="Overall score">
                    {sc.overall_score ? <span className="px-2 py-0.5 rounded bg-brand-900/50 text-brand-300 text-xs font-medium">{sc.overall_score}/5</span> : '—'}
                  </ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Recommendation">
                    {sc.recommendation ? <span className={`text-xs font-semibold ${RECOMMENDATION_COLORS[sc.recommendation]}`}>{RECOMMENDATION_LABELS[sc.recommendation]}</span> : '—'}
                  </ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Interviewer">{sc.interviewer_name ?? '—'}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Submitted">{formatDateTimeGmt(sc.submitted_at)}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Round">{sc.round}</ApplicationDetailAttrRow>
                </dl>
                {sc.rubric.length > 0 && sc.rubric.some((r) => r.score !== null) && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-2 font-semibold">Rubric</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {sc.rubric.filter((r) => r.score !== null).map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-1.5 text-xs rounded vscode-bg-tertiary px-2 py-1">
                          <span className="vscode-text-secondary truncate">{r.criterion}</span>
                          <span className="text-brand-300 font-medium shrink-0">{r.score}/5</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {sc.rubric.some(r => r.note) && (
                  <div className="mb-2">
                    <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Criterion Notes</p>
                    <div className="space-y-1">
                      {sc.rubric.filter(r => r.note).map((r, i) => (
                        <div key={i} className="text-xs"><span className="vscode-text-tertiary">{r.criterion}:</span> <span className="vscode-text-secondary">{r.note}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {sc.notes && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Overall Notes</p>
                    <p className="text-xs vscode-text-secondary leading-relaxed">{sc.notes}</p>
                  </div>
                )}
              </ApplicationDetailAttrGroup>
            ))}
          </div>
          );
        })()}

        {/* ═══════ PARAMETERS TAB — full width, no sidebar ═══════ */}
        {activeTab === 'parameters' && (
          <div className="space-y-4">

            <ApplicationDetailAttrGroup title="Object">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Name">
                  <span className="font-medium">
                    {[app.candidate?.full_name, app.job?.title].filter(Boolean).join(' @ ') || '—'}
                  </span>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Object type">
                  <span className="vscode-text-secondary">Application (read-only)</span>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Description" ddClassName="lg:col-span-3 min-w-0">
                  <textarea
                    readOnly
                    tabIndex={-1}
                    rows={5}
                    value={app.internal_notes?.trim() ? app.internal_notes : ''}
                    placeholder="—"
                    aria-label="Description"
                    className="ggj-param-readonly-textarea"
                  />
                </ApplicationDetailAttrRow>
              </dl>
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title="Linked Objects">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Candidate">
                  <Link to={`/candidates/${app.candidate_id}`} className="text-brand-400 hover:underline">
                    {app.candidate?.full_name ?? app.candidate_id}
                  </Link>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Candidate email">{app.candidate?.email ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Candidate location">{app.candidate?.location ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job">
                  <Link to={`/jobs/${app.job_id}`} className="text-brand-400 hover:underline">{app.job?.title ?? app.job_id}</Link>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job department">{app.job?.department ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job location">{app.job?.location ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client">
                  <Link to={`/clients/${app.client_id}`} className="text-brand-400 hover:underline">{app.client?.name ?? app.client_id}</Link>
                </ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client industry">{app.client?.industry ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client contact">{app.client?.contact_name ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client email">{app.client?.contact_email ?? '—'}</ApplicationDetailAttrRow>
              </dl>
            </ApplicationDetailAttrGroup>

            {app.rejection_reason_code && (
              <ApplicationDetailAttrGroup title="Rejection Details">
                <dl className="ggj-attr-grid text-sm">
                  <ApplicationDetailAttrRow label="Reason"><span className="text-red-300">{REJECTION_REASON_LABELS[app.rejection_reason_code]}</span></ApplicationDetailAttrRow>
                  {app.rejection_reason_text && <ApplicationDetailAttrRow label="Detail">{app.rejection_reason_text}</ApplicationDetailAttrRow>}
                  <ApplicationDetailAttrRow label="Status at rejection">{STATUS_LABEL[app.status]}</ApplicationDetailAttrRow>
                </dl>
              </ApplicationDetailAttrGroup>
            )}

            {optimization && (
              <ApplicationDetailAttrGroup title="Optimization Record">
                <dl className="ggj-attr-grid text-sm">
                  <ApplicationDetailAttrRow label="Optimization ID"><span className="font-mono text-xs break-all">{optimization.id}</span></ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="ATS score before">{optimization.ats_score_before ?? '—'}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="ATS score after">{optimization.ats_score_after != null ? <span className="text-green-400 font-medium">{optimization.ats_score_after}</span> : '—'}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Score delta">{optimization.ats_score_before != null && optimization.ats_score_after != null
                    ? <span className="vscode-text-primary font-medium">
                        {optimization.ats_score_after > optimization.ats_score_before ? '+' : ''}{optimization.ats_score_after - optimization.ats_score_before}
                      </span>
                    : '—'}
                  </ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Alias used">{optimization.alias_used ?? '—'}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Watermark ref"><span className="font-mono text-xs break-all">{optimization.watermark_ref ?? '—'}</span></ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Masked PDF">{optimization.masked_resume_url ? <span className="vscode-text-primary">Available</span> : <span className="vscode-text-tertiary">Not generated</span>}</ApplicationDetailAttrRow>
                  <ApplicationDetailAttrRow label="Created">{optimization.created_at ? formatDateTimeGmt(optimization.created_at) : '—'}</ApplicationDetailAttrRow>
                </dl>
              </ApplicationDetailAttrGroup>
            )}

            <ApplicationDetailAttrGroup title="Pipeline Lock & Access">
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Lock held by you">{pipelineLockHeld ? 'Yes' : 'No'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Locked by peer">{pipelineLockedByPeer ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Edit mode">{isEditing ? 'Active' : 'View only'}</ApplicationDetailAttrRow>
                {pipelineLockBootstrapError && <ApplicationDetailAttrRow label="Lock note"><span className="vscode-text-secondary">{pipelineLockBootstrapError}</span></ApplicationDetailAttrRow>}
              </dl>
            </ApplicationDetailAttrGroup>

            <ApplicationDetailAttrGroup title="System Attributes">
              <p className="text-xs vscode-text-tertiary mb-3">Immutable audit fields — these cannot be edited here.</p>
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Application ID"><span className="font-mono text-xs break-all">{app.id}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Org ID"><span className="font-mono text-xs break-all">{app.org_id}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Candidate ID"><span className="font-mono text-xs break-all">{app.candidate_id}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Job ID"><span className="font-mono text-xs break-all">{app.job_id}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Client ID"><span className="font-mono text-xs break-all">{app.client_id}</span></ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Submitted">{formatDateTimeGmt(app.submitted_at)}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Last modified">{app.updated_at ? formatDateTimeGmt(app.updated_at) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Assigned to">{app.assigned_user?.display_name ?? app.assigned_user?.email ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Source">{app.source ?? '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Pipeline stage">{STATUS_LABEL[app.status]}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Interview count">{interviewEvents.length}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Scorecard count">{scorecards.length}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Email thread count">{emailThreads.length}</ApplicationDetailAttrRow>
              </dl>
            </ApplicationDetailAttrGroup>
          </div>
        )}

        </div>{/* end LEFT column */}

        {/* ── RIGHT: context-aware sidebar — hidden on Parameters tab ── */}
        {activeTab !== 'parameters' && (
          <aside className="ggj-rail-aside space-y-4 lg:sticky lg:top-4 lg:self-start">

            {/* ── OVERVIEW TAB sidebar ── */}
            {activeTab === 'overview' && (
              <>
                {/* Timeline first — most relevant for overview */}
                {id && <ApplicationHeartbeatTimeline applicationId={id} timeline={timeline} nextActionIndex={nextActionIndex} />}

                {/* Status & pipeline actions */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-ov-status`} title="Status & Actions" defaultOpen>
                  <div className="space-y-3 px-1">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Pipeline</p>
                      <ApplicationStatusChip status={app.status} />
                    </div>
                    {nextSteps.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-2 font-semibold">Move To</p>
                        <div className="space-y-1">
                          {nextSteps.slice(0, 5).map((s) => (
                            <button key={s} type="button"
                              className="w-full text-left text-xs px-2 py-1.5 rounded vscode-bg-tertiary hover:vscode-bg-hover vscode-text-primary transition-colors disabled:opacity-40"
                              disabled={updatingStatus || Boolean(pipelineLockedByPeer)}
                              onClick={() => handleStatusChange(s)}>
                              → {STATUS_LABEL[s]}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {pipelineLockedByPeer && (
                      <p className="text-[10px] text-amber-400 bg-amber-900/20 rounded px-2 py-1">Locked by {pipelineLockedByPeer}</p>
                    )}
                  </div>
                </CandidateCollapsiblePanel>

                {/* Quick links for overview */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-ov-links`} title="Quick Links" defaultOpen>
                  <div className="space-y-0.5 px-1">
                    {[
                      app.candidate_id && { key: 'cand', to: `/candidates/${app.candidate_id}`, label: app.candidate?.full_name ?? 'Candidate' },
                      app.job_id && { key: 'job',  to: `/jobs/${app.job_id}`, label: `${app.job?.title ?? 'Job'} @ ${app.client?.name ?? ''}` },
                      app.client_id && { key: 'cli', to: `/clients/${app.client_id}`, label: app.client?.name ?? 'Client' },
                      { key: 'mail', to: `/emails/compose?applicationId=${id}`, label: 'Compose email' },
                    ].filter(Boolean).map((l: { key: string; to: string; label: string } | false) => l && (
                      <Link key={(l as { key: string }).key} to={(l as { to: string }).to}
                        className="flex items-center justify-between gap-2 py-1.5 text-sm text-brand-400 hover:text-brand-300">
                        <span className="truncate">{(l as { label: string }).label}</span>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                      </Link>
                    ))}
                  </div>
                </CandidateCollapsiblePanel>
              </>
            )}

            {/* ── EMAILS TAB sidebar ── */}
            {activeTab === 'emails' && (
              <>
                {/* Timeline only — action buttons are in the toolbar */}
                {id && <ApplicationHeartbeatTimeline applicationId={id} timeline={timeline} nextActionIndex={nextActionIndex} />}

                {/* Engagement stats */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-em-stats`} title="Engagement" defaultOpen>
                  <div className="space-y-1.5 px-1 text-xs">
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Sent</span><span className="vscode-text-primary font-medium">{emailThreads.length}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Opened</span><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.open_count > 0).length}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Responded</span><span className="vscode-text-primary font-medium">{emailThreads.filter(t => t.cta_response).length}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Awaiting</span><span className="font-medium" style={{ color: 'var(--vscode-text-link)' }}>{emailThreads.filter(t => !t.cta_response && t.sent_at).length}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Total opens</span><span className="vscode-text-primary font-medium">{emailThreads.reduce((s, t) => s + (t.open_count || 0), 0)}</span></div>
                  </div>
                </CandidateCollapsiblePanel>
              </>
            )}

            {/* ── FITMENT TAB sidebar ── */}
            {activeTab === 'fitment' && (
              <>
                <CandidateCollapsiblePanel storageKey={`app-${id}-fit-links`} title="Quick Links" defaultOpen>
                  <div className="space-y-0.5 px-1">
                    {[
                      { key: 'opt',  to: `/single-optimize?applicationId=${id}`,            label: 'Optimize Resume' },
                      app.candidate_id && { key: 'cand', to: `/candidates/${app.candidate_id}`, label: app.candidate?.full_name ?? 'Candidate' },
                      app.job_id && { key: 'job',  to: `/jobs/${app.job_id}`,                   label: app.job?.title ?? 'Job' },
                    ].filter(Boolean).map((l: { key: string; to: string; label: string } | false) => l && (
                      <Link key={(l as { key: string }).key} to={(l as { to: string }).to}
                        className="flex items-center justify-between gap-2 py-1.5 text-sm text-brand-400 hover:text-brand-300">
                        <span className="truncate">{(l as { label: string }).label}</span>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                      </Link>
                    ))}
                  </div>
                </CandidateCollapsiblePanel>

                <CandidateCollapsiblePanel storageKey={`app-${id}-fit-info`} title="ATS Summary" defaultOpen>
                  <div className="space-y-1.5 px-1 text-xs">
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Score before</span><span className="vscode-text-primary font-medium">{optimization?.ats_score_before ?? '—'}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Score after</span><span className="vscode-text-primary font-medium">{optimization?.ats_score_after ?? '—'}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Fitment</span><span className="vscode-text-primary font-medium">{app.fitment_score ?? '—'}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Masked PDF</span><span className="vscode-text-primary font-medium">{optimization?.masked_resume_url ? 'Ready' : 'Not generated'}</span></div>
                  </div>
                </CandidateCollapsiblePanel>
              </>
            )}

            {/* ── INTERVIEWS TAB sidebar ── */}
            {activeTab === 'interviews' && (
              <>
                {/* Scheduling Pulse first */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-int-sched`} title="Scheduling Pulse" defaultOpen>
                  <div className="space-y-2.5 px-1">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1.5 font-semibold">Pipeline</p>
                      <ApplicationStatusChip status={app.status} />
                    </div>
                    <div className="text-xs space-y-1">
                      <p className="font-medium vscode-text-secondary">
                        {nextInterviewSummary?.scheduled_at
                          ? (new Date(nextInterviewSummary.scheduled_at).getTime() >= Date.now() ? 'Next up' : 'Last scheduled')
                          : 'No slot confirmed yet'}
                      </p>
                      {nextInterviewSummary?.scheduled_at && (
                        <>
                          <p className="vscode-text-primary font-semibold">{formatInterviewRecordDate(nextInterviewSummary)}</p>
                          <p className="vscode-text-tertiary">Round {nextInterviewSummary.round}</p>
                        </>
                      )}
                    </div>
                    <div className="pt-1.5 border-t vscode-border space-y-1 text-xs">
                      <div className="flex justify-between"><span className="vscode-text-tertiary">Total rounds</span><span className="vscode-text-primary font-medium">{interviewEvents.length || '—'}</span></div>
                  <div className="flex justify-between"><span className="vscode-text-tertiary">Conducted</span><span className="vscode-text-primary font-medium">{interviewEvents.filter(e => e.status === 'conducted').length || '—'}</span></div>
                    <div className="flex justify-between"><span className="vscode-text-tertiary">Upcoming</span><span className="vscode-text-primary font-medium">{interviewEvents.filter(e => e.status === 'scheduled').length || '—'}</span></div>
                    </div>
                  </div>
                </CandidateCollapsiblePanel>

                {/* Interview Rounds — compact list in sidebar */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-int-rounds`} title="Interview Rounds" defaultOpen>
                  {sortedInterviewEvents.length === 0 ? (
                    <p className="text-xs vscode-text-tertiary px-1 py-2 text-center">No rounds scheduled yet</p>
                  ) : (
                    <div className="space-y-1.5 px-1">
                      {sortedInterviewEvents.map((ev) => {
                        const statusColor = ev.status === 'conducted' ? '#22c55e' : ev.status === 'cancelled' ? '#6b7280' : ev.status === 'no_show' ? '#ef4444' : ev.status === 'draft' || ev.status === 'awaiting_booking' ? '#94a3b8' : '#f59e0b';
                        return (
                          <div key={ev.id} className="rounded-lg border p-2 text-xs"
                            style={interviewCalSelectedEventId === ev.id
                              ? { background: 'color-mix(in srgb, var(--vscode-text-link) 10%, var(--vscode-bg-elevated))', borderColor: 'color-mix(in srgb, var(--vscode-text-link) 38%, var(--vscode-border-primary))' }
                              : { background: 'var(--vscode-bg-secondary)', borderColor: 'var(--vscode-border-primary)' }}>
                            <div className="flex items-center justify-between gap-1 mb-0.5">
                              <span className="font-semibold vscode-text-primary">R{ev.round}</span>
                              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium capitalize"
                                style={{ background: `${statusColor}18`, color: statusColor }}>
                                {ev.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                            {ev.scheduled_at && (
                              <p className="vscode-text-secondary tabular-nums">{formatInterviewRecordDate(ev)}</p>
                            )}
                            {ev.interviewer_name && (
                              <p className="vscode-text-tertiary truncate">Lead: {ev.interviewer_name}</p>
                            )}
                            <div className="flex gap-1 mt-1.5 flex-wrap">
                              {ev.status === 'scheduled' && (
                                <button type="button" className="text-[10px] px-2 py-0.5 rounded"
                                  style={{ background: '#16a34a20', color: '#22c55e' }}
                                  disabled={Boolean(pipelineLockedByPeer)}
                                  onClick={async () => {
                                    if (!id || !app || pipelineLockedByPeer) return;
                                    try {
                                      await interviewEventService.update(ev.id, { status: 'conducted' });
                                      const doneStatus: ApplicationStatus = ev.round >= 2 ? 'interview_2_conducted' : 'interview_1_conducted';
                                      const { updated_at } = await applicationService.updateStatus(id, doneStatus, `Interview round ${ev.round} completed`, undefined, undefined, { expectedUpdatedAt: app.updated_at });
                                      setApp((prev) => (prev ? { ...prev, status: doneStatus, updated_at } : prev));
                                      setInterviewEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, status: 'conducted' } : e)));
                                    } catch (e) {
                                      if (isApplicationStatusConflictError(e)) { setStatusConcurrencyMessage(e.message); const fresh = await applicationService.getById(id); setApp(fresh); setInterviewEvents(await interviewEventService.listByApplication(id)); }
                                      else { console.error(e); setStatusConcurrencyMessage(e instanceof Error ? e.message : 'Could not update.'); }
                                    }
                                  }}>Done</button>
                              )}
                              <button type="button"
                                className="text-[10px] px-2 py-0.5 rounded flex items-center gap-0.5"
                                style={{ background: 'color-mix(in srgb, var(--vscode-text-link) 12%, var(--vscode-bg-elevated))', color: 'var(--vscode-text-link)' }}
                                disabled={Boolean(pipelineLockedByPeer)}
                                title="Edit this interview round"
                                onClick={() => {
                                  setInterviewCalSelectedEventId(ev.id);
                                  openEditEvent(ev);
                                }}>
                                <Edit2 className="w-2.5 h-2.5" /> Edit
                              </button>
                              {ev.scheduled_at && (
                                <button className="text-[10px] px-2 py-0.5 rounded vscode-bg-tertiary vscode-text-secondary" type="button"
                                  title="Download .ics"
                                  onClick={() => { const cname = app?.candidate?.full_name ?? 'Candidate'; const jtitle = app?.job?.title ?? 'Interview'; downloadICS(`interview_round${ev.round}_${ev.scheduled_at!.slice(0, 10)}`, [{ uid: ev.id, summary: `Round ${ev.round}: ${cname} — ${jtitle}`, description: ev.notes ?? '', dtstart: new Date(ev.scheduled_at!), dtend: new Date(new Date(ev.scheduled_at!).getTime() + 3600000) }]); }}>
                                  .ics
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CandidateCollapsiblePanel>

                {/* Timeline */}
                {id && <ApplicationHeartbeatTimeline applicationId={id} timeline={timeline} nextActionIndex={nextActionIndex} />}

                {/* Prep links */}
                <CandidateCollapsiblePanel storageKey={`app-${id}-int-links`} title="Quick Links" defaultOpen>
                  <div className="space-y-0.5 px-1">
                    {[
                      { key: 'prep', to: `/interviews/prep?candidateId=${app.candidate_id}&jobId=${app.job_id}`, label: 'Prep & scorecards' },
                      { key: 'ws',   to: `/interviews/candidate/${app.candidate_id}?applicationId=${id}`,        label: 'Interview Workspace' },
                      app.candidate_id && { key: 'cand', to: `/candidates/${app.candidate_id}`,                  label: app.candidate?.full_name ?? 'Candidate' },
                    ].filter(Boolean).map((l: { key: string; to: string; label: string } | false) => l && (
                      <Link key={(l as { key: string }).key} to={(l as { to: string }).to}
                        className="flex items-center justify-between gap-2 py-1.5 text-sm text-brand-400 hover:text-brand-300">
                        <span className="truncate">{(l as { label: string }).label}</span>
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                      </Link>
                    ))}
                  </div>
                </CandidateCollapsiblePanel>

                {scorecards.length > 0 && (
                  <CandidateCollapsiblePanel storageKey={`app-${id}-sc-summary`} title="Scorecard Summary" defaultOpen>
                    <div className="space-y-1.5 px-1 text-xs">
                      <div className="flex justify-between"><span className="vscode-text-tertiary">Scorecards</span><span className="vscode-text-primary font-medium">{scorecards.length}</span></div>
                      <div className="flex justify-between"><span className="vscode-text-tertiary">Avg score</span>
                        <span className="vscode-text-primary font-medium">
                          {scorecards.filter(s => s.overall_score != null).length > 0
                            ? (scorecards.filter(s => s.overall_score != null).reduce((sum, s) => sum + (s.overall_score ?? 0), 0) / scorecards.filter(s => s.overall_score != null).length).toFixed(1)
                            : '—'}/5
                        </span>
                      </div>
                      <div className="flex justify-between"><span className="vscode-text-tertiary">Latest rec.</span>
                        <span className="vscode-text-primary font-medium capitalize">{scorecards[0]?.recommendation?.replace(/_/g, ' ') ?? '—'}</span>
                      </div>
                      <div className="flex justify-between"><span className="vscode-text-tertiary">Interviews</span><span className="vscode-text-primary font-medium">{interviewEvents.length}</span></div>
                    </div>
                  </CandidateCollapsiblePanel>
                )}
              </>
            )}
          </aside>
        )}

      </div>{/* end two-column grid */}

      {aiAssistField === 'internal' && id && (
        <ContextAiAssistModal
          open
          onClose={() => setAiAssistField(null)}
          orgId={user?.orgId}
          feature="ai_assist_application_internal_notes"
          preset="application_internal_notes"
          title="AI — internal notes"
          contextHint={`Candidate: ${app.candidate?.full_name ?? '—'}\nJob: ${app.job?.title ?? '—'}\nClient: ${app.client?.name ?? '—'}`}
          original={internalNotes}
          onApply={(text, mode) => {
            const next =
              mode === 'replace'
                ? text
                : internalNotes.trim()
                  ? `${internalNotes.trim()}\n\n${text}`
                  : text;
            setInternalNotes(next);
            void applicationService.updateNotes(id, next).then(() => {
              setApp((a) => (a ? { ...a, internal_notes: next || null } : a));
            });
          }}
        />
      )}

      {aiAssistField === 'interview' && (
        <ContextAiAssistModal
          open
          onClose={() => setAiAssistField(null)}
          orgId={user?.orgId}
          feature="ai_assist_interview_event_notes"
          preset="interview_event_notes"
          title="AI — interview notes"
          contextHint={`Candidate: ${app.candidate?.full_name ?? '—'}\nJob: ${app.job?.title ?? '—'}\nRound: ${intForm.round}\nWhen: ${intForm.scheduled_at || '—'}\nLocation: ${intForm.location || '—'}`}
          original={intForm.notes}
          onApply={(text, mode) =>
            setIntForm((f) => ({
              ...f,
              notes:
                mode === 'replace'
                  ? text
                  : f.notes.trim()
                    ? `${f.notes.trim()}\n\n${text}`
                    : text,
            }))
          }
        />
      )}

      {aiAssistField === 'scorecard' && (
        <ContextAiAssistModal
          open
          onClose={() => setAiAssistField(null)}
          orgId={user?.orgId}
          feature="ai_assist_scorecard_notes"
          preset="scorecard_notes"
          title="AI — scorecard notes"
          contextHint={`Round: ${scorecardRound}\nScore: ${scorecardScore === '' ? '—' : scorecardScore}\nRecommendation: ${scorecardRec || '—'}`}
          original={scorecardNotes}
          onApply={(text, mode) =>
            setScorecardNotes(
              mode === 'replace'
                ? text
                : scorecardNotes.trim()
                  ? `${scorecardNotes.trim()}\n\n${text}`
                  : text
            )
          }
        />
      )}

      {aiAssistField === 'offer' && id && app && (
        <ContextAiAssistModal
          open
          onClose={() => setAiAssistField(null)}
          orgId={user?.orgId}
          feature="ai_assist_application_offer_notes"
          preset="offer_comp_draft"
          title="AI — offer draft (internal)"
          contextHint={`Candidate: ${app.candidate?.full_name ?? '—'}\nJob: ${app.job?.title ?? '—'}\nClient: ${app.client?.name ?? '—'}\nStatus: ${STATUS_LABEL[app.status]}\nInternal notes excerpt:\n${(internalNotes || '').slice(0, 800)}`}
          original={offerNotes}
          onApply={(text, mode) => {
            const next =
              mode === 'replace'
                ? text
                : offerNotes.trim()
                  ? `${offerNotes.trim()}\n\n${text}`
                  : text;
            setOfferNotes(next);
            void applicationService.updateOfferNotes(id, next).then(() => {
              setApp((a) => (a ? { ...a, offer_notes: next || null } : a));
            });
          }}
        />
      )}

      {aiAssistField === 'screening' && id && app && (
        <ContextAiAssistModal
          open
          onClose={() => setAiAssistField(null)}
          orgId={user?.orgId}
          feature="ai_assist_screening_summary"
          preset="screening_fitment_summary"
          title="AI — screening summary"
          contextHint={`Candidate: ${app.candidate?.full_name ?? '—'}\nJob: ${app.job?.title ?? '—'}\nFitment score: ${app.fitment_score ?? '—'}\nFitment JSON (truncated):\n${JSON.stringify(app.fitment_json ?? {}, null, 0).slice(0, 3500)}`}
          original=""
          onApply={(text, mode) => {
            const next =
              mode === 'replace'
                ? text
                : internalNotes.trim()
                  ? `${internalNotes.trim()}\n\n--- Screening summary ---\n${text}`
                  : `--- Screening summary ---\n${text}`;
            setInternalNotes(next);
            void applicationService.updateNotes(id, next).then(() => {
              setApp((a) => (a ? { ...a, internal_notes: next || null } : a));
            });
          }}
        />
      )}

      {/* ── Email Thread Detail Modal — vertical stepper + right tips ── */}
      {emailDetailThread && (() => {
        const th = emailDetailThread;
        const replies = emailThreads
          .filter(t => t.in_reply_to_thread_id === th.id)
          .sort((a, b) => new Date(a.sent_at ?? a.created_at).getTime() - new Date(b.sent_at ?? b.created_at).getTime());

        // Build stepper steps from email lifecycle events
        type StepStatus = 'done' | 'active' | 'pending' | 'awaiting';
        interface StepItem {
          key: string;
          label: string;
          sublabel?: string;
          timestamp?: string | null;
          status: StepStatus;
          icon: React.ReactNode;
          content?: React.ReactNode;
        }

        const steps: StepItem[] = [];

        // Step 1: Sent (always first)
        steps.push({
          key: 'sent',
          label: 'Email Sent',
          sublabel: `To: ${th.recipient_name || th.recipient_email}`,
          timestamp: th.sent_at,
          status: th.sent_at ? 'done' : 'pending',
          icon: <Send className="w-3.5 h-3.5" />,
          content: (
            <div className="space-y-2">
              <div className="rounded-xl border vscode-border overflow-hidden">
                <div className="px-4 py-2 vscode-bg-tertiary border-b vscode-border flex items-center gap-2">
                  <span className="text-xs font-medium vscode-text-secondary">Subject:</span>
                  <span className="text-xs vscode-text-primary font-medium">{th.subject || '(No subject)'}</span>
                </div>
                <div className="px-4 py-3 max-h-56 overflow-y-auto">
                  {th.body_html
                    ? <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: th.body_html }} />
                    : <p className="text-sm vscode-text-tertiary italic">No body content.</p>}
                </div>
              </div>
            </div>
          ),
        });

        // Step 2: Opened (if opened_at exists or open_count > 0)
        if (th.opened_at || th.open_count > 0) {
          steps.push({
            key: 'opened',
            label: 'Email Opened',
            sublabel: th.open_count > 1 ? `${th.open_count}× opened` : 'Opened',
            timestamp: th.opened_at,
            status: 'done',
            icon: <MailOpen className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="First opened">{th.opened_at ? formatTimelineDate(th.opened_at) : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Total opens">{th.open_count}</ApplicationDetailAttrRow>
              </dl>
            ),
          });
        }

        // Steps for follow-ups
        if (th.followup_count > 0) {
          steps.push({
            key: 'followup',
            label: `Follow-up${th.followup_count > 1 ? 's' : ''} Sent`,
            sublabel: `${th.followup_count} follow-up${th.followup_count > 1 ? 's' : ''}`,
            timestamp: th.next_followup_at,
            status: 'done',
            icon: <Reply className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Follow-ups sent">{th.followup_count}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Next follow-up">{th.next_followup_at ? formatTimelineDate(th.next_followup_at) : th.followup_stopped ? 'Stopped' : '—'}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Auto follow-up">{th.followup_stopped ? 'Stopped' : 'Active'}</ApplicationDetailAttrRow>
              </dl>
            ),
          });
        }

        // Steps for replies
        replies.forEach((r, i) => {
          steps.push({
            key: `reply-${r.id}`,
            label: `Reply ${i + 1}`,
            sublabel: r.recipient_name || r.recipient_email,
            timestamp: r.sent_at,
            status: 'done',
            icon: <MessageSquare className="w-3.5 h-3.5" />,
            content: (
              <div className="rounded-xl border vscode-border overflow-hidden">
                <div className="px-4 py-2 vscode-bg-tertiary border-b vscode-border">
                  <span className="text-xs vscode-text-primary">{r.subject || '(No subject)'}</span>
                </div>
                <div className="px-4 py-3 max-h-48 overflow-y-auto">
                  {r.body_html
                    ? <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: r.body_html }} />
                    : <p className="text-sm vscode-text-tertiary italic">No body.</p>}
                </div>
              </div>
            ),
          });
        });

        // Final step: CTA Response or Awaiting
        if (th.cta_response) {
          steps.push({
            key: 'cta',
            label: `Client Responded — ${CTA_LABEL[th.cta_response]}`,
            sublabel: th.cta_response_note ?? undefined,
            timestamp: th.cta_responded_at,
            status: 'done',
            icon: th.cta_response === 'accept' ? <CheckCircle className="w-3.5 h-3.5" /> : th.cta_response === 'reject' ? <XCircle className="w-3.5 h-3.5" /> : <HelpCircle className="w-3.5 h-3.5" />,
            content: (
              <dl className="ggj-attr-grid text-sm">
                <ApplicationDetailAttrRow label="Response">{CTA_LABEL[th.cta_response]}</ApplicationDetailAttrRow>
                <ApplicationDetailAttrRow label="Responded at">{th.cta_responded_at ? formatTimelineDate(th.cta_responded_at) : '—'}</ApplicationDetailAttrRow>
                {th.cta_response_note && <ApplicationDetailAttrRow label="Note">{th.cta_response_note}</ApplicationDetailAttrRow>}
              </dl>
            ),
          });
        } else if (th.sent_at) {
          steps.push({
            key: 'awaiting',
            label: 'Awaiting Reply',
            sublabel: 'No response yet',
            timestamp: null,
            status: 'awaiting',
            icon: <Clock className="w-3.5 h-3.5" />,
            content: (
              <div className="space-y-2">
                <p className="text-xs vscode-text-secondary">The client has not responded to the CTA link yet.</p>
                <Link to={`/emails/compose?applicationId=${id}&replyTo=${th.id}`}
                  className="text-xs text-brand-400 hover:underline inline-flex items-center gap-1">
                  <Reply className="w-3 h-3" /> Send follow-up
                </Link>
              </div>
            ),
          });
        }

        // Quick tips for the right panel
        const TIPS = [
          { icon: <Eye className="w-3.5 h-3.5" />, text: 'Track when the client opens your email via the open count.' },
          { icon: <Zap className="w-3.5 h-3.5" />, text: 'CTA buttons let the client shortlist, pass, or query directly from the email.' },
          { icon: <Reply className="w-3.5 h-3.5" />, text: 'Send a follow-up if no response after 48 hours to keep the process moving.' },
          { icon: <AlertCircle className="w-3.5 h-3.5" />, text: 'Opened but no response usually means the client is reviewing — give it 1–2 days.' },
          { icon: <Inbox className="w-3.5 h-3.5" />, text: 'Replies are linked back to this thread for a complete communication trail.' },
        ];

        return (
          <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 bg-black/60 backdrop-blur-md overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) setEmailDetailThread(null); }}>
            <div className="w-full max-w-4xl my-6 rounded-2xl overflow-hidden shadow-2xl border vscode-border flex flex-col"
              style={{ background: 'var(--vscode-bg-secondary, #1e1e2e)' }}>

              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-6 py-4 shrink-0"
                style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--vscode-text-link) 92%, #312e81) 0%, color-mix(in srgb, var(--vscode-text-link) 55%, #1e1b4b) 100%)' }}>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-white/60 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Email Communication
                  </p>
                  <h2 className="text-base font-bold text-white leading-tight">{th.subject || '(No subject)'}</h2>
                  <p className="text-xs text-white/65 mt-1">
                    To: {th.recipient_name || th.recipient_email}
                    {th.sent_at && ` · ${formatTimelineDate(th.sent_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/emails/thread/${th.id}`} target="_blank"
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                  <button type="button" onClick={() => setEmailDetailThread(null)}
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Body: two-column — stepper left, tips right */}
              <div className="flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0 max-h-[72vh]">

                {/* LEFT: Vertical stepper */}
                <div className="flex-1 overflow-y-auto px-6 py-5 min-w-0">
                  <p className="text-xs font-semibold vscode-text-tertiary uppercase tracking-wide mb-4">Communication Timeline</p>

                  <div className="space-y-0">
                    {steps.map((step, idx) => {
                      const isLast    = idx === steps.length - 1;
                      const isOpen    = emailAccordionOpen[step.key] ?? false;
                      const isDone    = step.status === 'done';
                      const isAwaiting = step.status === 'awaiting';

                      // circle color & icon
                      const circleStyle = isDone
                        ? { background: 'var(--vscode-text-link)', border: '2px solid var(--vscode-text-link)', color: '#fff' }
                        : isAwaiting
                        ? { background: 'transparent', border: '2px solid #f59e0b', color: '#f59e0b' }
                        : { background: 'transparent', border: '2px solid var(--vscode-border-primary)', color: 'var(--vscode-text-tertiary)' };

                      return (
                        <div key={step.key} className="flex gap-4">
                          {/* Connector column */}
                          <div className="flex flex-col items-center" style={{ width: 28 }}>
                            {/* Circle */}
                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10 relative"
                              style={circleStyle}>
                              {isDone
                                ? (idx === 0 ? step.icon : <CheckCircle className="w-3.5 h-3.5" />)
                                : isAwaiting
                                ? <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                                : step.icon}
                            </div>
                            {/* Vertical line */}
                            {!isLast && (
                              <div className="flex-1 w-px mt-1"
                                style={{ background: isDone ? 'var(--vscode-text-link)' : 'var(--vscode-border-primary)', minHeight: isOpen ? 16 : 32 }} />
                            )}
                          </div>

                          {/* Step content */}
                          <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-4'}`}>
                            {/* Step header — clickable accordion */}
                            <button type="button"
                              className="w-full text-left flex items-start justify-between gap-2 group"
                              onClick={() => setEmailAccordionOpen(prev => ({ ...prev, [step.key]: !prev[step.key] }))}>
                              <div className="min-w-0">
                                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDone ? '' : isAwaiting ? 'text-amber-400' : 'vscode-text-tertiary'}`}
                                  style={{ color: isDone ? 'var(--vscode-text-link)' : undefined }}>
                                  {isDone ? `Step ${idx + 1}` : isAwaiting ? 'Pending' : `Step ${idx + 1}`}
                                </p>
                                <p className={`text-sm font-semibold ${isDone ? 'vscode-text-primary' : isAwaiting ? 'text-amber-300' : 'vscode-text-tertiary'}`}>
                                  {step.label}
                                  {isAwaiting && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-amber-400 animate-heartbeat align-middle" />}
                                </p>
                                {step.sublabel && (
                                  <p className="text-xs vscode-text-tertiary mt-0.5">{step.sublabel}</p>
                                )}
                                {step.timestamp && (
                                  <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5" />
                                    {formatTimelineDate(step.timestamp)}
                                  </p>
                                )}
                              </div>
                              {step.content && (
                                <ChevronDown className={`w-4 h-4 vscode-text-tertiary shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                              )}
                            </button>

                            {/* Accordion expanded content */}
                            {step.content && isOpen && (
                              <div className="mt-3 rounded-xl border vscode-border overflow-hidden"
                                style={{ background: 'var(--vscode-bg-tertiary, #2a2a3e)' }}>
                                <div className="p-4">{step.content}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT: Quick tips */}
                <div className="w-full lg:w-[220px] shrink-0 border-t lg:border-t-0 lg:border-l vscode-border overflow-y-auto"
                  style={{ background: 'var(--vscode-bg-tertiary, #2a2a3e)' }}>
                  <div className="px-4 py-4 space-y-4">
                    {/* Current status */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                        style={{ color: 'var(--vscode-text-link)' }}>Status</p>
                      {th.cta_response ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium"
                          style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                          {CTA_ICON[th.cta_response]} {CTA_LABEL[th.cta_response]}
                        </span>
                      ) : th.sent_at ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium"
                          style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Awaiting reply
                        </span>
                      ) : (
                        <span className="text-xs vscode-text-tertiary">Draft</span>
                      )}
                    </div>

                    {/* Stats */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--vscode-text-link)' }}>Engagement</p>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Opens</span><span className="vscode-text-primary font-medium">{th.open_count || 0}</span></div>
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Follow-ups</span><span className="vscode-text-primary font-medium">{th.followup_count || 0}</span></div>
                        <div className="flex justify-between"><span className="vscode-text-tertiary">Replies</span><span className="vscode-text-primary font-medium">{replies.length}</span></div>
                      </div>
                    </div>

                    {/* Quick tips */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--vscode-text-link)' }}>Quick Tips</p>
                      <div className="space-y-2.5">
                        {TIPS.map((tip, i) => (
                          <div key={i} className="flex gap-2 text-[11px] vscode-text-tertiary leading-snug">
                            <span className="shrink-0 mt-0.5" style={{ color: 'var(--vscode-text-link)' }}>{tip.icon}</span>
                            <span>{tip.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-6 py-3 border-t vscode-border shrink-0"
                style={{ background: 'var(--vscode-bg-secondary, #1e1e2e)' }}>
                <Link to={`/emails/compose?applicationId=${id}&replyTo=${th.id}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5">
                  <Reply className="w-4 h-4" /> Reply
                </Link>
                <div className="flex items-center gap-2">
                  <Link to={`/emails/thread/${th.id}`} target="_blank"
                    className="btn-secondary text-sm inline-flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Full page
                  </Link>
                  <button type="button" onClick={() => setEmailDetailThread(null)} className="btn-secondary text-sm">Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

          </>
        }
      />

      {/* ── Rejection reason modal (APP-010) ─────────────────────── */}
      <OptimizedResumePreviewModal
        open={maskedResumePreview !== null}
        onClose={closeMaskedResumePreview}
        headline={maskedResumePreview?.headline ?? ''}
        payload={maskedResumePreview?.payload ?? null}
        eyebrow={maskedResumePreview?.eyebrow}
        consultancyWatermark={user?.orgName ?? null}
        downloadFileBase={app?.candidate?.full_name ?? undefined}
      />

      <ConfirmDialog
        open={deleteInterviewRoundOpen}
        onClose={() => setDeleteInterviewRoundOpen(false)}
        onConfirm={() => {
          setDeleteInterviewRoundOpen(false);
          if (!editingEventId || pipelineLockedByPeer) return;
          const eventId = editingEventId;
          setDeletingEventId(eventId);
          void (async () => {
            try {
              await interviewEventService.delete(eventId);
              setInterviewEvents((prev) => prev.filter((e) => e.id !== eventId));
              closeIntForm();
              setInterviewCalSelectedEventId(null);
            } catch (e) {
              console.error(e);
              setStatusConcurrencyMessage(e instanceof Error ? e.message : 'Could not delete.');
            } finally {
              setDeletingEventId(null);
            }
          })();
        }}
        title="Delete interview round?"
        message="Delete this interview round? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        requireTypedConfirm="DELETE"
      />

      {rejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="card w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold vscode-text-primary">
                {pendingRejectStatus === 'no_show' ? 'Mark as No-show' : 'Reject application'}
              </h2>
              <button onClick={() => setRejectModalOpen(false)} className="p-1 vscode-text-secondary hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="block text-xs vscode-text-secondary mb-1">Reason</label>
              <select
                className="input w-full"
                value={rejectCode}
                onChange={(e) => setRejectCode(e.target.value as RejectionReasonCode)}
              >
                {REJECTION_REASON_CODES.map((c) => (
                  <option key={c} value={c}>{REJECTION_REASON_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs vscode-text-secondary mb-1">Additional notes (optional)</label>
              <textarea
                className="input w-full h-20 resize-none"
                value={rejectText}
                onChange={(e) => setRejectText(e.target.value)}
                placeholder="Recruiter context…"
              />
            </div>
            <div className="flex gap-3 justify-end pt-1">
              <button onClick={() => setRejectModalOpen(false)} className="btn-secondary text-sm">
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={updatingStatus}
                className="btn-primary text-sm bg-red-600 hover:bg-red-500 border-red-600"
              >
                {updatingStatus ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
